import React, { useState, useEffect } from 'react';
import {
  MapPin, Users, GraduationCap, Building2, Home,
  ClipboardList, ShieldAlert, Scale, HandCoins, Landmark, Info, Pencil, ChevronDown, X
} from 'lucide-react';
import DISTRICT_DATA from '../data/districtData';
import HABITATION_METRICS from '../data/habitationMetrics';
import ST_HABITATION_SEA from '../data/stHabitationSea';
import DetailModal from '../Components/DetailModal';
import PerformancePieChart from '../Components/PerformancePieChart';
import ST_SOCIO_ECON    from '../data/stSocioEconomic';
import DROPOUT_METRICS from '../data/dropoutMetrics';
import { ADW_SCHOOLS, TW_SCHOOLS } from '../data/schoolMetrics';
import LAND_PATTAS_DISTRICT from '../data/landPattasDistrict';
import SCHOOL_RESULTS from '../data/schoolResults';
import LITERACY_RATE from '../data/literacyRate';
import FRA_DISTRICT_CODES from '../data/fraDistrictCodes';
import BASE_URL from '../services/api';
import ManualEntryModal from '../Components/ManualEntryModal';
import HabitationDetailModal from '../Components/HabitationDetailModal';
import { MANUAL_SECTIONS, lookupValue } from '../data/manualFields';

const MANUAL_API_KEY = 'A9x2mK_Lp5QvR8tN1wZ4jY7sB0uC3fE6hG9iI2kM5nO8pQ1rS4tU7vX0zW3yZ6aB';

const POA_API  = `${BASE_URL}/dashboard/poa`;
const LAND_API = `${BASE_URL}/dashboard/land`;
const FRA_API  = `${BASE_URL}/dashboard/fra`;
const TAHDCO_SCHEME_API = `${BASE_URL}/dashboard/tahdco-scheme`;
const TELP_API          = `${BASE_URL}/dashboard/telp`;
const TIME_API = `${BASE_URL}/dashboard/time`;
const NALLOSAI_HOSTEL_DETAILS_API = `${BASE_URL}/dashboard/nallosai/hostel-details`;
const NALLOSAI_HOSTEL_WISE_API    = `${BASE_URL}/dashboard/nallosai/hostel-wise`;
const NALLOSAI_ATTENDANCE_API     = `${BASE_URL}/dashboard/nallosai/attendance`;
const NALLOSAI_ADW_HOSTEL_DETAILS_API = `${BASE_URL}/dashboard/nallosai-adw/hostel-details`;
const NALLOSAI_ADW_HOSTEL_WISE_API    = `${BASE_URL}/dashboard/nallosai-adw/hostel-wise`;
const NALLOSAI_ADW_ATTENDANCE_API     = `${BASE_URL}/dashboard/nallosai-adw/attendance`;

// Nallosai uses its own district spellings — map to our ALL-CAPS dashboard names.
// Anything not in the map falls back to plain-uppercase match.
const NALLOSAI_DISTRICT_ALIASES = {
  'KANYAKUMARI':      'KANNIYAKUMARI',
  'KANCHIPURAM':      'KANCHEEPURAM',
  'RANIPETTAI':       'RANIPET',
  'NILGIRIS':         'THE NILGIRIS',
  'THIRUCHIRAPALLI':  'TIRUCHIRAPPALLI',
  'THIRUCHIRAPPALLI': 'TIRUCHIRAPPALLI',
  'THIRUVALLUR':      'TIRUVALLUR',
  'THIRUVANNAMALAI':  'TIRUVANNAMALAI',
  'THIRUVARUR':       'TIRUVARUR',
  'TIRUPATTUR':       'TIRUPATHUR',
  'TUTICORIN':        'THOOTHUKKUDI',
  'VILLUPURAM':       'VILLUPURAM',
};
const nallosaiToDashDistrict = (name) => {
  const u = String(name || '').trim().toUpperCase();
  return NALLOSAI_DISTRICT_ALIASES[u] || u;
};

const fmt = (n) => (n == null ? 'N/A' : new Intl.NumberFormat('en-IN').format(n));
const fmtInr = (n) => (n == null ? 'N/A' : `₹${new Intl.NumberFormat('en-IN').format(n)}`);
const NA  = 'N/A';

// Ranking helper: given a Map/Object of {districtName -> numericValue}, return the
// currently-selected district's tier for colour-coding.
//   direction 'higherIsBetter' → top third = green
//   direction 'lowerIsBetter'  → bottom third = green
// Returns 'top' | 'mid' | 'bot' | null (no data).
// When currentDistrict is empty (state view) we use the SPREAD across districts:
//   if the bottom tercile is significantly worse than the median → red.
//   if the top tercile is meaningfully better than the median → green.
//   otherwise amber. This gives the state cell a meaningful colour even without a specific district selected.
const rankTier = (valuesByDistrict, currentDistrict, direction = 'higherIsBetter') => {
  const entries = Object.entries(valuesByDistrict || {})
    .map(([d, v]) => [d, Number(v)])
    .filter(([, v]) => Number.isFinite(v));
  if (entries.length < 3) return null;
  entries.sort((a, b) => direction === 'higherIsBetter' ? b[1] - a[1] : a[1] - b[1]);
  const cutTop = Math.ceil(entries.length / 3);
  const cutBot = entries.length - cutTop;

  // A specific district is selected — return its tier.
  if (currentDistrict) {
    const rankIdx = entries.findIndex(([d]) => d === currentDistrict);
    if (rankIdx < 0) return null;
    if (rankIdx < cutTop) return 'top';
    if (rankIdx >= cutBot) return 'bot';
    return 'mid';
  }

  // State view — colour by how tightly the top / bottom terciles bracket the median.
  // If the worst tercile's average is a lot worse than the median → red.
  // If the best tercile's average is a lot better than the median → green.
  // Otherwise amber.
  const values = entries.map(e => e[1]);
  const median = values[Math.floor(values.length / 2)];
  const topAvg = values.slice(0, cutTop).reduce((a, b) => a + b, 0) / cutTop;
  const botAvg = values.slice(cutBot).reduce((a, b) => a + b, 0) / (values.length - cutBot);
  // Distance from median normalised by median (fallback to raw diff if median is 0).
  const denom = Math.abs(median) || 1;
  const topGap = Math.abs(topAvg - median) / denom;
  const botGap = Math.abs(botAvg - median) / denom;
  // If bottom tercile is markedly worse than median (>30 % of median distance), red.
  if (botGap > 0.3) return 'bot';
  if (topGap > 0.3) return 'top';
  return 'mid';
};

// Map a tier → Tailwind classes for the table cell background + text.
// Cell keeps its normal font colour; a coloured left-border stripe indicates rank.
// Bottom tier ('bot') gets a stronger treatment — client wants low-performing
// (critical) cells to jump out.
const rankCellClass = (tier) => {
  if (tier === 'top') return 'bg-emerald-50 border-l-4 border-emerald-500';
  if (tier === 'mid') return 'bg-amber-50 border-l-4 border-amber-500';
  if (tier === 'bot') return 'bg-rose-100 border-l-4 border-rose-600 ring-1 ring-inset ring-rose-300';
  return '';
};

// Convenience for use inside a cell's className with the base cell class.
const withRank = (base, tier) => `${base} ${rankCellClass(tier)}`.trim();

// Registry of "pink" ranking metrics — { sectionKey: { fieldKey: 'higher' | 'lower' } }.
// Direction says whether higher or lower is better performance.
// Used by RankedManualCell wrapper — no need to touch each cell inline.
const PINK_METRICS = {
  // Section 5 — Hostel ADW
  sec5_hostel_adw: {
    boys_actual: 'higher', girls_actual: 'higher', total_actual: 'higher',
    boys_vacant_pct: 'lower', girls_vacant_pct: 'lower', total_vacant_pct: 'lower',
    w_gen_vacant_pct: 'lower', w_sc_vacant_pct: 'lower', w_st_vacant_pct: 'lower', w_total_vacant_pct: 'lower',
    avg_attendance_pct: 'higher', students_below_75_pct: 'lower',
    inspections_pending_pct: 'lower', st_inspections_pending_pct: 'lower',
    complaints_last_6mo: 'lower', complaints_resolved: 'higher',
    hostels_zero_attendance_pct: 'lower',
  },
  // Section 5(a) — Hostel TW (same fields as ADW)
  sec5a_hostel_tw: {
    boys_actual: 'higher', girls_actual: 'higher', total_actual: 'higher',
    boys_vacant_pct: 'lower', girls_vacant_pct: 'lower', total_vacant_pct: 'lower',
    w_gen_vacant_pct: 'lower', w_sc_vacant_pct: 'lower', w_st_vacant_pct: 'lower', w_total_vacant_pct: 'lower',
    avg_attendance_pct: 'higher', students_below_75_pct: 'lower',
    inspections_pending_pct: 'lower', st_inspections_pending_pct: 'lower',
    complaints_last_6mo: 'lower', complaints_resolved: 'higher',
    hostels_zero_attendance_pct: 'lower',
  },
  // Section 6 — Pattas (Row B: manual + Excel-backed static per-district data)
  sec6_pattas_manual: {
    eligible_not_given:  'lower',
    rejected_petitions:  'lower',
    not_scrutinised:     'lower',
  },
  // Section 7 — Court Cases
  sec7_court_cases: {
    pending: 'lower', more_than_1yr: 'lower', contempt: 'lower', ca_not_filed: 'lower',
  },
  // Section 8 — CM Cell Petitions
  sec8_cm_cell: {
    pending: 'lower', rating_b: 'lower', rating_c: 'lower',
  },
  // Section 9 — RTI
  sec9_rti: {
    pending: 'lower', appeal1: 'lower', appeal_commission: 'lower',
  },
  // Section 10 — Pending files at Govt
  sec10_pending_files: {
    total: 'lower', more_1yr: 'lower', more_3yr: 'lower', more_5yr: 'lower',
  },
  // Section 11.1 — Burial SC
  sec11_1_burial_sc: {
    without_dedicated: 'lower', without_pathways: 'lower', encroachments: 'lower',
  },
  // Section 11.2 — Burial ST
  sec11_2_burial_st: {
    without_dedicated: 'lower', without_pathways: 'lower', encroachments: 'lower',
  },
  // Section 12 — Transport / Water
  sec12_transport_water: {
    no_public_transport: 'lower', no_all_weather: 'lower', no_drinking_water: 'lower',
    no_mobile: 'lower', no_electricity_hab: 'lower', no_electricity_st_hh: 'lower',
  },
  // Section 14 — Buildings
  sec14_buildings: {
    y20_30: 'lower', y30_40: 'lower', y40_plus: 'lower', stability_cert: 'higher',
  },
  // Section 17 — DAPSC/ST DMC
  sec17_dap: {
    q1_status: 'higher', q2_status: 'higher', q3_status: 'higher', q4_status: 'higher',
  },
  // Section 18a — Atrocity/Awareness
  sec18_vmc_manual: {
    atrocity_prone_reviewed: 'higher', awareness_programmes: 'higher',
  },
  // Section 19 — DW Committee
  sec19_dw_committee: {
    meetings_held: 'higher', petitions_received: 'higher', petitions_unresolved: 'lower',
  },
  // Section 20 — Cleanliness
  sec20_cleanliness: {
    meetings_held: 'higher', members_2025_26: 'higher', members_2026_27: 'higher',
  },
  // Section 21 — Community Certificate
  sec21_community_cert: {
    total_pending: 'lower', pending_dsp: 'lower', pending_anthro: 'lower', pending_rdo: 'lower',
  },
  // Section 23 — Loan Applications (per-scheme cells; using API for received/approved/pending/rejected)
  sec23_loan_applications: {},
  // Section 24 — Construction (per-portal cells for not_started / under_const are ranked)
  sec24_construction: {},
  // Section 25 — FRA — pending fields ranked lower=better
  sec25_fra: {
    frc_pending: 'lower', pending_survey: 'lower', pending_resolution_sdclc: 'lower',
    pending_sdlc: 'lower', pending_dlc: 'lower', titles_pending: 'lower',
    claims_received: 'higher', frc_completed: 'higher',
    recommended_survey: 'higher', recommended_sdlc: 'higher',
    recommended_dlc: 'higher', approved_dlc: 'higher', titles_issued: 'higher',
  },
};

/** Section header — TN Social Justice Dept theme: deep navy strip on white body */
const Section = ({ num, title, source, children, sectionKey, canEdit, onEdit }) => (
  <div id={`section-${num}`} className="border border-slate-200 bg-white shadow-sm scroll-mt-24">
    <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2.5 bg-[#0f2e4d] border-b border-slate-300">
      <div className="flex items-baseline gap-2.5">
        <span className="text-[11px] font-bold text-blue-200 uppercase tracking-widest">{num}</span>
        <h3 className="text-sm font-semibold text-white tracking-wide">{title}</h3>
      </div>
      <div className="flex items-center gap-2">
        {source && (
          <span className="text-[10px] text-blue-100 uppercase tracking-wide font-medium border border-white/25 bg-white/10 px-2 py-0.5">
            {source}
          </span>
        )}
        {canEdit && sectionKey && MANUAL_SECTIONS[sectionKey] && (
          <button
            onClick={() => onEdit(sectionKey)}
            title="Edit values"
            className="p-1.5 text-white hover:bg-white/20 border border-white/30 bg-white/10 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

/** Row of key/value pairs, used for most KPI rows */
const KPIRow = ({ items }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
    {items.map((it, i) => (
      <div key={i} className="bg-white border border-gray-200 px-3 py-2">
        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold truncate" title={it.label}>{it.label}</p>
        <p className={`text-base font-bold mt-0.5 truncate ${it.value === NA ? 'text-gray-400' : 'text-gray-800'}`}>{it.value}</p>
      </div>
    ))}
  </div>
);

// State-wide aggregate for the top header row (Static Data from Excel)
const stateHeader = (() => {
  let blocks = 0, villages = 0;
  DISTRICT_DATA.forEach(d => {
    if (typeof d.blocks === 'number') blocks += d.blocks;
    villages += d.scstVillages40plus || 0;
  });
  // SC/ST habitation totals come from the SC/ST Habitations sheet (source of
  // truth) — districtData.js has many nulls so summing it undercounts (13755
  // vs the true 13798).
  const scHab = HABITATION_METRICS?.state?.sc_habitations || 0;
  const stHab = HABITATION_METRICS?.state?.st_habitations || 0;
  // Top 3 districts with highest SC + ST population share (proxy for "high concentration blocks")
  const top3 = [...DISTRICT_DATA]
    .map(d => ({
      name: d.name,
      total: (d.population?.sc?.count || 0) + (d.population?.st?.count || 0),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 3)
    .map(d => d.name)
    .join(', ');
  return { blocks, villages, scHab, stHab, top3 };
})();

/** Compact top-header card — soft toned variants */
const HeaderCard = ({ label, value, tone = 'slate', onClick }) => {
  const tones = {
    slate:   'bg-slate-50 border-slate-200',
    blue:    'bg-blue-50 border-blue-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    rose:    'bg-rose-50 border-rose-200',
    violet:  'bg-violet-50 border-violet-200',
    teal:    'bg-teal-50 border-teal-200',
    indigo:  'bg-indigo-50 border-indigo-200',
  };
  const isText = typeof value === 'string' && value.length > 12;
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      title={clickable ? 'Click to view details' : label}
      className={`border p-3 ${tones[tone] || tones.slate} ${clickable ? 'cursor-pointer hover:shadow-md hover:border-[#0f2e4d] transition-shadow' : ''}`}
    >
      <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold leading-tight">{label}</p>
      <p
        className={`${isText ? 'text-sm leading-snug' : 'text-lg truncate'} font-bold text-gray-800 mt-1`}
        title={String(value)}
      >
        {value}
      </p>
    </div>
  );
};

const CURRENT_YEAR = 2026;
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3, CURRENT_YEAR - 4];
// District list — source of truth is the SC/ST Habitations sheet
// (falls back to old districtData names only if habitations has none)
// Full 38-district master list (Tamil Nadu). We show every district in the
// picker even if the habitations sheet or some other data source doesn't yet
// cover it — cells that lack data simply render as N/A.
const TN_DISTRICTS = [
  'ARIYALUR','CHENGALPATTU','CHENNAI','COIMBATORE','CUDDALORE','DHARMAPURI','DINDIGUL','ERODE',
  'KALLAKURICHI','KANCHEEPURAM','KANNIYAKUMARI','KARUR','KRISHNAGIRI','MADURAI','MAYILADUTHURAI',
  'NAGAPATTINAM','NAMAKKAL','PERAMBALUR','PUDUKKOTTAI','RAMANATHAPURAM','RANIPET','SALEM',
  'SIVAGANGAI','TENKASI','THANJAVUR','THE NILGIRIS','THENI','THOOTHUKKUDI','TIRUCHIRAPPALLI',
  'TIRUNELVELI','TIRUPATHUR','TIRUPPUR','TIRUVALLUR','TIRUVANNAMALAI','TIRUVARUR','VELLORE',
  'VILLUPURAM','VIRUDHUNAGAR',
];
const DISTRICT_OPTIONS = ['', ...TN_DISTRICTS.slice().sort()];

const IntegratedDashboard = () => {
  // Read logged-in user (super-admin or DADWO) — the auto-district defaulting
  // for DADWOs uses `user.district` set on the login response.
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const isSuperAdmin = !!currentUser.is_superadmin;

  // Filters
  // District auto-selection precedence:
  //   1. ?district=NAME URL param (from District Ranking page navigation)
  //   2. Logged-in user's assigned district (DADWO logins)
  //   3. '' → All Districts (state view — super-admins default here)
  const initialDistrict = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('district');
      if (q && HABITATION_METRICS.districts[q.toUpperCase()]) return q.toUpperCase();
    } catch (e) { /* noop */ }
    const userDist = (currentUser.district || '').toUpperCase();
    if (userDist && HABITATION_METRICS.districts[userDist]) return userDist;
    return '';
  })();
  const [district, setDistrict] = useState(initialDistrict);   // '' = All districts (state-wide)
  // Non-superadmin (DADWO) users are locked to their own district — hide the picker.
  const districtLocked = !isSuperAdmin && !!currentUser.district;
  // POA-only date-range filters (all optional — empty = no filter, totals shown)
  const [registrationFrom, setRegistrationFrom] = useState('');
  const [registrationTo,   setRegistrationTo]   = useState('');
  const [disbursementFrom, setDisbursementFrom] = useState('');
  const [disbursementTo, setDisbursementTo] = useState('');

  const [poaData, setPoaData] = useState(null);
  const [poaLoading, setPoaLoading] = useState(false);
  const [poaError, setPoaError] = useState(null);
  const [fraData, setFraData] = useState(null);
  const [fraLoading, setFraLoading] = useState(false);
  const [fraError, setFraError] = useState(null);
  const [tahdcoData, setTahdcoData] = useState(null);
  const [tahdcoLoading, setTahdcoLoading] = useState(false);
  const [tahdcoError, setTahdcoError] = useState(null);
  // TELP — Education loan applications (NSKFDC / NSTFDC / NSFDC) district-wise
  const [telpData, setTelpData] = useState(null);
  const [telpLoading, setTelpLoading] = useState(false);
  const [telpError, setTelpError] = useState(null);

  // Generic drill-down modal — one state re-used by every clickable count
  // outside FRA. Shape: { title, subtitle, rows, columns } or null.
  const [drillDown, setDrillDown] = useState(null);
  const openDrill = (title, rows, columns, subtitle = '') =>
    setDrillDown({ title, subtitle, rows, columns });
  const [timeData, setTimeData] = useState(null);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState(null);
  // Nallosai — TW hostel details, hostel-wise metadata, attendance
  const [nallosaiHostelDetails, setNallosaiHostelDetails] = useState([]);
  const [nallosaiHostelWise,    setNallosaiHostelWise]    = useState([]);
  const [nallosaiAttendance,    setNallosaiAttendance]    = useState([]);
  const [nallosaiLoading,       setNallosaiLoading]       = useState(false);
  const [nallosaiError,         setNallosaiError]         = useState(null);
  // Nallosai ADW — same three endpoints, ADWSSO path
  const [nallosaiAdwHostelDetails, setNallosaiAdwHostelDetails] = useState([]);
  const [nallosaiAdwHostelWise,    setNallosaiAdwHostelWise]    = useState([]);
  const [nallosaiAdwAttendance,    setNallosaiAdwAttendance]    = useState([]);
  const [nallosaiAdwLoading,       setNallosaiAdwLoading]       = useState(false);
  const [nallosaiAdwError,         setNallosaiAdwError]         = useState(null);
  // All-district FRA data for rank colouring — { [district]: row }
  const [fraByDistrict, setFraByDistrict] = useState({});
  // FRA drill-down modal state
  const [fraDetailOpen, setFraDetailOpen]       = useState(false);
  const [fraDetailTitle, setFraDetailTitle]     = useState('');
  const [fraDetailLoading, setFraDetailLoading] = useState(false);
  const [fraDetailError, setFraDetailError]     = useState(null);
  const [fraDetailRows, setFraDetailRows]       = useState([]);
  // Pagination + search for the FRA drill-down (46k+ rows possible)
  const [fraDetailPage, setFraDetailPage]       = useState(1);
  const [fraDetailQuery, setFraDetailQuery]     = useState('');
  const FRA_DETAIL_PAGE_SIZE = 50;

  // Filter predicate per count-type. 3009 returns all claim records for the
  // district — we narrow to those relevant to the clicked count.
  const FRA_FILTERS = {
    'Total Claims Received':          () => true,
    'FRC Verification Completed':     r => (r.FRC_RECOMMENDATION_STATUS || '').toLowerCase() === 'recommended',
    'FRC Verification Pending':       r => (r.FRC_RECOMMENDATION_STATUS || '').toLowerCase() === 'not recommended',
    'Claims Recommended for Survey':  r => (r.FRC_RECOMMENDATION_STATUS || '').toLowerCase() === 'recommended',
    'Claims Pending for Survey':      r => (r.SURVEY_VERIFICATION_ST || '').toLowerCase() === 'pending',
    'Claims Recommended to SDLC':     r => (r.STATUS_OF_GS || '').toLowerCase() !== 'pending',
    'Claims Pending for Resolution':  r => (r.STATUS_OF_GS || '').toLowerCase() === 'pending',
    'Claims Recommended to DLC':      r => (r.STATUS_OF_SDLC || '').toLowerCase() !== 'pending',
    'Claims Pending with SDLC':       r => (r.STATUS_OF_SDLC || '').toLowerCase() === 'pending',
    'Claims Approved by DLC':         r => (r.STATUS_OF_DLC || '').toLowerCase() === 'approved',
    'Claims Pending with DLC':        r => (r.STATUS_OF_DLC || '').toLowerCase() === 'pending',
    'Titles Issued':                  r => (r.TITLE_STATUS || '').toLowerCase().includes('issued') && !(r.TITLE_STATUS || '').toLowerCase().includes('not'),
    'Titles Pending':                 r => (r.TITLE_STATUS || '').toLowerCase().includes('not issued'),
  };

  const openFraDetail = async (label) => {
    setFraDetailOpen(true);
    setFraDetailTitle(label);
    setFraDetailLoading(true);
    setFraDetailError(null);
    setFraDetailRows([]);
    setFraDetailPage(1);
    setFraDetailQuery('');
    try {
      const code = district ? FRA_DISTRICT_CODES[district] : '';
      const url = code
        ? `${FRA_API}?typeid=3009&input01=${encodeURIComponent(code)}`
        : `${FRA_API}?typeid=3009`;
      const res = await fetch(url);
      const json = await res.json();
      if (!json.success) {
        setFraDetailError(json.message || 'FRA detail API returned no data');
      } else {
        const data = json.data;
        const all = Array.isArray(data) ? data
                  : Array.isArray(data?.data) ? data.data
                  : data && typeof data === 'object' ? [data]
                  : [];
        const filter = FRA_FILTERS[label] || (() => true);
        setFraDetailRows(all.filter(filter));
      }
    } catch (err) {
      setFraDetailError(err.message || 'Failed to load FRA detail');
    } finally {
      setFraDetailLoading(false);
    }
  };
  const [landData, setLandData] = useState(null);
  const [landLoading, setLandLoading] = useState(false);
  const [landError, setLandError] = useState(null);

  // Manual entries persisted in DB
  const [manualEntries, setManualEntries] = useState([]);
  // Cross-district manual entries (used for rank colouring) — { [district]: [entries] }
  const [manualByDistrict, setManualByDistrict] = useState({});
  const [editingSection, setEditingSection] = useState(null);

  // Header-card drill-down (opens HabitationDetailModal)
  const [drill, setDrill] = useState(null); // { kind }

  // isSuperAdmin already computed at the top of the component (line ~342) — reused for pencil icons.

  const manualDistrict = district || 'STATE';

  // Fetch every district's manual entries once — powers cross-district rank colouring.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/dashboard/manual?district=ALL`, {
          headers: { 'x-api-key': MANUAL_API_KEY },
        });
        const json = await res.json();
        if (!cancelled && json.success) {
          const grouped = {};
          (json.data || []).forEach(r => {
            const d = r.district || 'STATE';
            if (!grouped[d]) grouped[d] = [];
            grouped[d].push(r);
          });
          setManualByDistrict(grouped);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch saved manual entries whenever district changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/dashboard/manual?district=${encodeURIComponent(manualDistrict)}`, {
          headers: { 'x-api-key': MANUAL_API_KEY },
        });
        const json = await res.json();
        if (!cancelled && json.success) setManualEntries(json.data || []);
      } catch (e) {
        // silent — dashboard still works, just no manual values
      }
    })();
    return () => { cancelled = true; };
  }, [manualDistrict]);

  // Lookup helper — reads a saved manual value for (section, field)
  const mv = (sectionKey, fieldKey) => {
    const meta = MANUAL_SECTIONS[sectionKey];
    const period = meta?.period || '';
    const v = lookupValue(manualEntries, sectionKey, fieldKey, period);
    return (v === null || v === undefined || v === '') ? null : v;
  };

  // Format a manual value with fmt() if the field is numeric, else pass-through
  const mvFmt = (sectionKey, fieldKey) => {
    const v = mv(sectionKey, fieldKey);
    if (v === null) return NA;
    const meta = MANUAL_SECTIONS[sectionKey];
    const fieldDef = meta?.fields?.find(f => f.key === fieldKey);
    if (fieldDef && fieldDef.type === 'number') {
      const n = Number(v);
      return isNaN(n) ? v : fmt(n);
    }
    return v;
  };

  // Cross-district map of manual values for a (section, field) pair — feeds rankTier().
  // Only districts with a numeric saved value are included.
  const manualDistrictMap = (sectionKey, fieldKey) => {
    const meta = MANUAL_SECTIONS[sectionKey];
    const period = meta?.period || '';
    const out = {};
    Object.entries(manualByDistrict).forEach(([d, entries]) => {
      if (d === 'STATE') return;
      const v = lookupValue(entries, sectionKey, fieldKey, period);
      const n = Number(v);
      if (Number.isFinite(n)) out[d] = n;
    });
    return out;
  };

  // Compute the ranking-tier class for a pink manual field. Returns '' if not
  // registered in PINK_METRICS or state view. Also considers a static-data
  // source (Excel) — if the field appears in LAND_PATTAS_DISTRICT it uses those
  // per-district values as the ranking baseline, with manual overrides layered on.
  const rankedManual = (sectionKey, fieldKey) => {
    const direction = PINK_METRICS[sectionKey]?.[fieldKey];
    if (!direction) return '';
    const dir = direction === 'higher' ? 'higherIsBetter' : 'lowerIsBetter';
    // Start with manual entries.
    const map = manualDistrictMap(sectionKey, fieldKey);
    // Layer in static-source Excel data for section 6 pattas.
    if (sectionKey === 'sec6_pattas_manual') {
      Object.entries(LAND_PATTAS_DISTRICT.districts || {}).forEach(([d, row]) => {
        if (map[d] !== undefined) return;
        const v = row?.[fieldKey];
        if (v != null && Number.isFinite(Number(v))) map[d] = Number(v);
      });
    }
    return rankCellClass(rankTier(map, district, dir));
  };

  // Rank tier for a live FRA cell — reads across fraByDistrict. `apiKey` is the
  // UPPERCASE FRA field (e.g. 'FRC_VERIFICATION_PENDING'). `mkey` maps into
  // PINK_METRICS['sec25_fra'] to decide direction.
  const rankedFra = (mkey, apiKey) => {
    const direction = PINK_METRICS.sec25_fra?.[mkey];
    if (!direction) return '';
    const map = {};
    Object.entries(fraByDistrict).forEach(([d, row]) => {
      const v = row?.[apiKey];
      if (v != null && Number.isFinite(Number(v))) map[d] = Number(v);
    });
    const dir = direction === 'higher' ? 'higherIsBetter' : 'lowerIsBetter';
    return rankCellClass(rankTier(map, district, dir));
  };

  // Initial values to pre-fill the modal when opening
  const modalInitialValues = editingSection ? (() => {
    const meta = MANUAL_SECTIONS[editingSection];
    if (!meta) return {};
    const initVal = {};
    meta.fields.forEach(f => {
      const v = lookupValue(manualEntries, editingSection, f.key, meta.period || '');
      if (v !== null && v !== undefined) initVal[f.key] = v;
    });
    return initVal;
  })() : {};

  // Called after modal saves — refetch entries
  const refetchManual = async () => {
    try {
      const res = await fetch(`${BASE_URL}/dashboard/manual?district=${encodeURIComponent(manualDistrict)}`, {
        headers: { 'x-api-key': MANUAL_API_KEY },
      });
      const json = await res.json();
      if (json.success) setManualEntries(json.data || []);
    } catch (e) { /* silent */ }
  };

  // POA — respects filters. Empty filters mean no restriction — POA returns totals.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPoaLoading(true); setPoaError(null); setPoaData(null);
      try {
        const params = new URLSearchParams();
        if (district)         params.set('district', district);
        if (registrationFrom) params.set('registration_from', registrationFrom);
        if (registrationTo)   params.set('registration_to',   registrationTo);
        if (disbursementFrom) params.set('relief_disbursement_from', disbursementFrom);
        if (disbursementTo)   params.set('relief_disbursement_to',   disbursementTo);
        const res = await fetch(`${POA_API}?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          if (json.success && json.data) setPoaData(json.data);
          else setPoaError('POA API returned no data');
        }
      } catch (err) {
        if (!cancelled) setPoaError(err.message || 'Failed to load POA data');
      } finally { if (!cancelled) setPoaLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [district, registrationFrom, registrationTo, disbursementFrom, disbursementTo]);

  // Land — no filters supported yet; keep state-wide
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLandLoading(true); setLandError(null); setLandData(null);
      try {
        const res = await fetch(LAND_API);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setLandData(json);
      } catch (err) {
        if (!cancelled) setLandError(err.message || 'Failed to load Land data');
      } finally { if (!cancelled) setLandLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // FRA — POST-based upstream, proxied. TYPEID=3007 (state) or 3007+INPUT_01=<code> for district.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFraLoading(true); setFraError(null); setFraData(null);
      try {
        const code = district ? FRA_DISTRICT_CODES[district] : '';
        const params = new URLSearchParams({ typeid: '3007' });
        if (code) params.set('input01', code);
        const res = await fetch(`${FRA_API}?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          if (json.success) setFraData(json.data);
          else setFraError(json.message || 'FRA API returned no data');
        }
      } catch (err) {
        if (!cancelled) setFraError(err.message || 'Failed to load FRA data');
      } finally { if (!cancelled) setFraLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [district]);

  // TAHDCO Scheme Summary — proxied POST, no filters supported yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTahdcoLoading(true); setTahdcoError(null); setTahdcoData(null);
      try {
        const res = await fetch(TAHDCO_SCHEME_API);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          if (json.success && json.data) setTahdcoData(json.data);
          else setTahdcoError(json.message || 'TAHDCO returned no data');
        }
      } catch (err) {
        if (!cancelled) setTahdcoError(err.message || 'Failed to load TAHDCO data');
      } finally { if (!cancelled) setTahdcoLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // TELP — District-wise Education Loan summary (NSKFDC / NSTFDC / NSFDC).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTelpLoading(true); setTelpError(null); setTelpData(null);
      try {
        const res = await fetch(TELP_API);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          if (json.success && json.data) setTelpData(json.data);
          else setTelpError(json.message || 'TELP returned no data');
        }
      } catch (err) {
        if (!cancelled) setTelpError(err.message || 'Failed to load TELP data');
      } finally { if (!cancelled) setTelpLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-fetch FRA data for every district — powers cross-district rank colouring.
  // Fetch in batches of 4 with a per-request timeout so a slow upstream doesn't
  // stall the tab (all 38 in parallel was overwhelming the FRA server).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = Object.entries(FRA_DISTRICT_CODES);
      const BATCH = 4;
      const REQ_TIMEOUT_MS = 8000;
      const fetchOne = async ([name, code]) => {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), REQ_TIMEOUT_MS);
        try {
          const res = await fetch(`${FRA_API}?typeid=3007&input01=${code}`, { signal: ctrl.signal });
          if (!res.ok) return [name, null];
          const json = await res.json();
          const row = Array.isArray(json.data) && json.data[0] ? json.data[0] : null;
          return [name, row];
        } catch { return [name, null]; }
        finally { clearTimeout(timer); }
      };
      const map = {};
      for (let i = 0; i < entries.length; i += BATCH) {
        if (cancelled) return;
        const chunk = entries.slice(i, i + BATCH);
        const results = await Promise.all(chunk.map(fetchOne));
        results.forEach(([name, row]) => { if (row) map[name] = row; });
        if (!cancelled) setFraByDistrict({ ...map });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // TIME (Construction Progress Monitoring System) — state-wide fetch, filter client-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTimeLoading(true); setTimeError(null); setTimeData(null);
      try {
        const res = await fetch(TIME_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            division: [], district: [], MainCategory: [], Subcategory: [],
            contractorId: '', departmentIds: [], year: [],
            selectionType: 'string', costOrCount: 'string',
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        // Backend proxy returns { success, data: <upstream body> }; upstream has data: [...rows...]
        const rows = json?.data?.data;
        if (!cancelled) {
          if (json.success && Array.isArray(rows)) setTimeData(rows);
          else setTimeError('TIME returned no data');
        }
      } catch (err) {
        if (!cancelled) setTimeError(err.message || 'Failed to load TIME data');
      } finally { if (!cancelled) setTimeLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Nallosai (Tribal Hostels) — three endpoints, one shot at mount. Attendance defaults to the last 7 days.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setNallosaiLoading(true); setNallosaiError(null);
      const today = new Date();
      const to = today.toISOString().slice(0, 10);
      const fromD = new Date(today); fromD.setDate(fromD.getDate() - 7);
      const from = fromD.toISOString().slice(0, 10);
      try {
        const [detailsRes, wiseRes, attRes] = await Promise.all([
          fetch(NALLOSAI_HOSTEL_DETAILS_API),
          fetch(NALLOSAI_HOSTEL_WISE_API),
          fetch(`${NALLOSAI_ATTENDANCE_API}?fromDate=${from}&toDate=${to}`),
        ]);
        const [details, wise, att] = await Promise.all([detailsRes.json(), wiseRes.json(), attRes.json()]);
        if (cancelled) return;
        setNallosaiHostelDetails(details?.data?.data || []);
        setNallosaiHostelWise(wise?.data?.data || []);
        setNallosaiAttendance(att?.data?.data || []);
      } catch (err) {
        if (!cancelled) setNallosaiError(err.message || 'Failed to load Nallosai data');
      } finally { if (!cancelled) setNallosaiLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Nallosai ADW — mirror of TW, ADWSSO endpoints.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setNallosaiAdwLoading(true); setNallosaiAdwError(null);
      const today = new Date();
      const to = today.toISOString().slice(0, 10);
      const fromD = new Date(today); fromD.setDate(fromD.getDate() - 7);
      const from = fromD.toISOString().slice(0, 10);
      try {
        const [detailsRes, wiseRes, attRes] = await Promise.all([
          fetch(NALLOSAI_ADW_HOSTEL_DETAILS_API),
          fetch(NALLOSAI_ADW_HOSTEL_WISE_API),
          fetch(`${NALLOSAI_ADW_ATTENDANCE_API}?fromDate=${from}&toDate=${to}`),
        ]);
        const [details, wise, att] = await Promise.all([detailsRes.json(), wiseRes.json(), attRes.json()]);
        if (cancelled) return;
        setNallosaiAdwHostelDetails(details?.data?.data || []);
        setNallosaiAdwHostelWise(wise?.data?.data || []);
        setNallosaiAdwAttendance(att?.data?.data || []);
      } catch (err) {
        if (!cancelled) setNallosaiAdwError(err.message || 'Failed to load ADW Nallosai data');
      } finally { if (!cancelled) setNallosaiAdwLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // FRA field resolver — case-insensitive key match across common variants.
  // The upstream response shape isn't documented, so we try a list of alias keys
  // per field. First hit wins. Falls back to the manual-entry value.
  const fraLookup = (() => {
    const flat = {};
    const flatten = (obj, prefix = '') => {
      if (!obj || typeof obj !== 'object') return;
      if (Array.isArray(obj)) {
        // If it's an array of {key, value} pairs, index by key. If it's an
        // array of records, merge the first row so keys become directly usable.
        obj.forEach((row, i) => {
          if (row && typeof row === 'object' && ('key' in row) && ('value' in row)) {
            flat[String(row.key).toLowerCase()] = row.value;
          } else if (i === 0 && row && typeof row === 'object') {
            flatten(row, prefix);
          }
        });
        return;
      }
      Object.entries(obj).forEach(([k, v]) => {
        if (v && typeof v === 'object') flatten(v, `${prefix}${k}.`);
        else flat[k.toLowerCase()] = v;
      });
    };
    flatten(fraData);
    return flat;
  })();

  const fraVal = (aliases) => {
    for (const a of aliases) {
      const hit = fraLookup[a.toLowerCase()];
      // Treat 0 as a real value — FRA returns 0 for districts with no activity yet.
      if (hit !== undefined && hit !== null && hit !== '') return hit;
    }
    return undefined;
  };

  // For a given manual-field key on sec25_fra, try FRA API first (via aliases),
  // fall back to the manual-entry value formatted with mvFmt.
  const fraFmt = (manualKey, aliases) => {
    const v = fraVal(aliases);
    if (v !== undefined) {
      const n = Number(v);
      return isNaN(n) ? String(v) : fmt(n);
    }
    return mvFmt('sec25_fra', manualKey);
  };

  const poa = poaData || {};
  const cs  = poa.caseSummary?.dynamic || {};
  const rs  = poa.reliefSummary || {};
  const ms  = poa.meetingSummary || {};
  const kpi = poa.kpiSummary || {};
  const claims  = kpi.poaClaims || {};
  const money   = kpi.monetaryRelief || {};
  const addRel  = kpi.additionalRelief || {};
  const vmcMem  = kpi.vmcMembers || {};
  const vmcMtg  = kpi.vmcMeetings || {};

  const land = landData || {};
  const ep   = land.e_patta || {};
  const cc   = land.court_cases || {};
  const fhs  = land.free_house_site_pattas || {};

  return (
    <div className="space-y-4">
      {/* Header + top-level District filter */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0f2e4d]">Integrated Dashboard</h1>
        
        </div>
        <div className="w-full lg:w-80">
          <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">
            {districtLocked ? 'Your District' : 'Select District'}
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0f2e4d]" />
            {districtLocked ? (
              <div className="w-full pl-9 pr-3 py-2 border border-slate-300 text-sm bg-slate-50 font-semibold text-[#0f2e4d]" title="Your account is scoped to this district">
                {district}
              </div>
            ) : (
              <>
                <select
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  className="w-full appearance-none pl-9 pr-8 py-2 border border-slate-300 focus:outline-none focus:border-[#0f2e4d] focus:ring-1 focus:ring-[#0f2e4d] text-sm bg-white font-medium text-[#0f2e4d]"
                >
                  <option value="">All Districts (Tamil Nadu)</option>
                  {DISTRICT_OPTIONS.filter(Boolean).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </>
            )}
          </div>
        </div>
      </div>

      {/* District Performance Overview — 4-bucket pie chart (Excellent/Good/Needs Improvement/Critical) */}
      {(() => {
        // Score = (# metrics where district is top-third) - (# metrics where district is bottom-third)
        // Higher score = better performer. Uses metrics we already have loaded.
        const perDistrictScore = {};
        const addMetric = (map, direction) => {
          const entries = Object.entries(map || {}).filter(([, v]) => Number.isFinite(Number(v)));
          if (entries.length < 3) return;
          entries.sort((a, b) => direction === 'higherIsBetter' ? Number(b[1]) - Number(a[1]) : Number(a[1]) - Number(b[1]));
          const cutTop = Math.ceil(entries.length / 3);
          const cutBot = entries.length - cutTop;
          entries.forEach(([d], idx) => {
            if (!(d in perDistrictScore)) perDistrictScore[d] = 0;
            if (idx < cutTop) perDistrictScore[d] += 1;
            else if (idx >= cutBot) perDistrictScore[d] -= 1;
          });
        };
        // Section 4 school pass %
        try {
          const s = (key) => {
            const map = {};
            Object.entries(SCHOOL_RESULTS[key]?.districts || {}).forEach(([d, v]) => { if (v?.pass_pct != null) map[d] = v.pass_pct; });
            return map;
          };
          addMetric(s('10_adw'), 'higherIsBetter');
          addMetric(s('12_adw'), 'higherIsBetter');
          addMetric(s('10_tw'),  'higherIsBetter');
          addMetric(s('12_tw'),  'higherIsBetter');
        } catch { /* silent */ }
        // Section 6 land pattas
        try {
          const e = {}, n = {};
          Object.entries(LAND_PATTAS_DISTRICT?.districts || {}).forEach(([d, v]) => {
            if (v?.eligible_not_given != null) e[d] = v.eligible_not_given;
            if (v?.not_scrutinised    != null) n[d] = v.not_scrutinised;
          });
          addMetric(e, 'lowerIsBetter');
          addMetric(n, 'lowerIsBetter');
        } catch { /* silent */ }
        // Section 22 FRA — pending fields (lower = better)
        try {
          ['FRC_VERIFICATION_PENDING', 'CLAIMS_PENDING_RESOLUTION', 'CLAIMS_PENDING_SDLC', 'CLAIMS_PENDING_DLC', 'TITLES_PENDING'].forEach(field => {
            const map = {};
            Object.entries(fraByDistrict || {}).forEach(([d, row]) => { if (row?.[field] != null) map[d] = row[field]; });
            addMetric(map, 'lowerIsBetter');
          });
        } catch { /* silent */ }
        return (
          <PerformancePieChart
            title="District Performance Overview"
            scores={perDistrictScore}
            onSelectDistrict={setDistrict}
          />
        );
      })()}

      {/* Inference callout — commented out per request */}
      {false && district && (() => {
        const weakMetrics = [];
        const check = (label, map, direction) => {
          const tier = rankTier(map, district, direction);
          if (tier === 'bot') {
            const rawVal = map?.[district];
            weakMetrics.push({ label, value: rawVal });
          }
        };
        // Human-readable labels for pink-metric field keys used across sections.
        const SECTION_TITLE = {
          sec5_hostel_adw: 'Section 5 - Hostel ADW',
          sec5a_hostel_tw: 'Section 5(a) - Hostel TW',
          sec6_pattas_manual: 'Section 6 - Land Pattas',
          sec7_court_cases: 'Section 7 - Court Cases',
          sec8_cm_cell: 'Section 8 - CM Cell Petitions',
          sec9_rti: 'Section 9 - RTI',
          sec10_pending_files: 'Section 10 - Pending Files',
          sec11_1_burial_sc: 'Section 11.1 - SC Burial Grounds',
          sec11_2_burial_st: 'Section 11.2 - ST Burial Grounds',
          sec12_transport_water: 'Section 12 - Transport / Water',
          sec14_buildings: 'Section 14 - Buildings',
          sec17_dap: 'Section 14 - DMC Meetings',
          sec18_vmc_manual: 'Section 18 - Atrocity/VMC',
          sec19_dw_committee: 'Section 19 - DW Committee',
          sec20_cleanliness: 'Section 20 - Cleanliness',
          sec21_community_cert: 'Section 15 - Community Certificate',
          sec25_fra: 'Section 22 - FRA',
        };
        const FIELD_LABEL = {
          // Hostel
          boys_actual: 'Boys Actual Strength', girls_actual: 'Girls Actual Strength', total_actual: 'Total Actual Strength',
          boys_vacant_pct: 'Boys Vacant %', girls_vacant_pct: 'Girls Vacant %', total_vacant_pct: 'Total Vacant %',
          w_gen_vacant_pct: 'Warden General Vacant %', w_sc_vacant_pct: 'Warden SC Vacant %', w_st_vacant_pct: 'Warden ST Vacant %', w_total_vacant_pct: 'Warden Total Vacant %',
          avg_attendance_pct: 'Avg Attendance %', students_below_75_pct: 'Students below 75%',
          inspections_pending_pct: 'DADWO Inspections Pending', st_inspections_pending_pct: 'ST Inspections Pending',
          complaints_last_6mo: 'Complaints last 6 months', complaints_resolved: 'Complaints Resolved',
          hostels_zero_attendance_pct: 'Hostels with Zero Attendance %',
          // Pattas
          eligible_not_given: 'Eligible but not given Patta', rejected_petitions: 'Rejected Petitions', not_scrutinised: 'Applications not Scrutinised',
          // Court / CM / RTI
          pending: 'Pending', more_than_1yr: 'Cases > 1 year', contempt: 'Contempt Cases', ca_not_filed: 'CA not filed',
          rating_b: 'Rating B', rating_c: 'Rating C',
          appeal1: '1st Appeals', appeal_commission: 'Commission Appeals',
          // Pending files
          total: 'Total Pending', more_1yr: 'Pending > 1 year', more_3yr: 'Pending > 3 years', more_5yr: 'Pending > 5 years',
          // Burial / habitations
          without_dedicated: 'Habitations without dedicated Burial Grounds', without_pathways: 'Habitations without Pathways', encroachments: 'Burial Ground Encroachments',
          // Transport
          no_public_transport: 'Habitations without Public Transport', no_all_weather: 'Habitations without All-Weather Roads',
          no_drinking_water: 'Habitations without Drinking Water', no_mobile: 'Habitations without Mobile Network',
          no_electricity_hab: 'Habitations without Electricity', no_electricity_st_hh: 'ST Households without Electricity',
          // Buildings
          y20_30: 'Buildings 20-30 years', y30_40: 'Buildings 30-40 years', y40_plus: 'Buildings > 40 years', stability_cert: 'Stability Certificate',
          // DMC
          q1_status: 'Quarter 1 DMC Status', q2_status: 'Quarter 2 DMC Status', q3_status: 'Quarter 3 DMC Status', q4_status: 'Quarter 4 DMC Status',
          // VMC / DW / Cleanliness
          atrocity_prone_reviewed: 'Atrocity-prone areas Reviewed', awareness_programmes: 'Awareness Programmes',
          meetings_held: 'Meetings Held', petitions_received: 'Petitions Received', petitions_unresolved: 'Petitions Unresolved',
          members_2025_26: 'Members 2025-26', members_2026_27: 'Members 2026-27',
          // CC
          total_pending: 'Total Pending', pending_dsp: 'Pending with DSP', pending_anthro: 'Pending Anthropology', pending_rdo: 'Pending with RDO',
          // FRA
          frc_pending: 'FRC Verification Pending', pending_survey: 'Pending Survey',
          pending_resolution_sdclc: 'Pending Resolution (SDLC)', pending_sdlc: 'Pending with SDLC', pending_dlc: 'Pending with DLC',
          titles_pending: 'Titles Pending', claims_received: 'Claims Received', frc_completed: 'FRC Completed',
          recommended_survey: 'Recommended for Survey', recommended_sdlc: 'Recommended to SDLC', recommended_dlc: 'Recommended to DLC',
          approved_dlc: 'Approved by DLC', titles_issued: 'Titles Issued',
        };

        // Section 4 (live) — School results ADW & TW Class X/XII pass %
        try {
          const s10adw = {}, s12adw = {}, s10tw = {}, s12tw = {};
          Object.entries(SCHOOL_RESULTS['10_adw']?.districts || {}).forEach(([d, v]) => { if (v?.pass_pct != null) s10adw[d] = v.pass_pct; });
          Object.entries(SCHOOL_RESULTS['12_adw']?.districts || {}).forEach(([d, v]) => { if (v?.pass_pct != null) s12adw[d] = v.pass_pct; });
          Object.entries(SCHOOL_RESULTS['10_tw']?.districts  || {}).forEach(([d, v]) => { if (v?.pass_pct != null) s10tw[d]  = v.pass_pct; });
          Object.entries(SCHOOL_RESULTS['12_tw']?.districts  || {}).forEach(([d, v]) => { if (v?.pass_pct != null) s12tw[d]  = v.pass_pct; });
          check('Section 4 - Class X Pass % (ADW)',   s10adw, 'higherIsBetter');
          check('Section 4 - Class XII Pass % (ADW)', s12adw, 'higherIsBetter');
          check('Section 4 - Class X Pass % (TW)',    s10tw,  'higherIsBetter');
          check('Section 4 - Class XII Pass % (TW)',  s12tw,  'higherIsBetter');
        } catch { /* silent */ }

        // Section 3 — Dropouts (Excel) — lower is better
        try {
          const dAdw = {}, dTw = {}, dTot = {};
          Object.entries(DROPOUT_METRICS?.districts || {}).forEach(([d, v]) => {
            if (v?.adw?.total != null)   dAdw[d] = v.adw.total;
            if (v?.tw?.total != null)    dTw[d]  = v.tw.total;
            if (v?.total?.total != null) dTot[d] = v.total.total;
          });
          check('Section 3 - Drop-outs Total', dTot, 'lowerIsBetter');
          check('Section 3 - Drop-outs (ADW)', dAdw, 'lowerIsBetter');
          check('Section 3 - Drop-outs (TW)',  dTw,  'lowerIsBetter');
        } catch { /* silent */ }

        // Section 4 — Schools with Zero Strength (Excel)
        try {
          const zAdw = {}, zTw = {};
          Object.entries(ADW_SCHOOLS?.districts || {}).forEach(([d, v]) => { if (v?.schools_zero != null) zAdw[d] = v.schools_zero; });
          Object.entries(TW_SCHOOLS?.districts  || {}).forEach(([d, v]) => { if (v?.schools_zero != null) zTw[d]  = v.schools_zero; });
          check('Section 4 - ADW Schools with Zero Strength', zAdw, 'lowerIsBetter');
          check('Section 4 - TW Schools with Zero Strength',  zTw,  'lowerIsBetter');
        } catch { /* silent */ }

        // Section 6 — Land Pattas (Excel)
        try {
          const eligNg = {}, notScr = {}, vac = {};
          Object.entries(LAND_PATTAS_DISTRICT?.districts || {}).forEach(([d, v]) => {
            if (v?.eligible_not_given != null) eligNg[d] = v.eligible_not_given;
            if (v?.not_scrutinised    != null) notScr[d] = v.not_scrutinised;
            if (v?.vacant_plot        != null) vac[d]    = v.vacant_plot;
          });
          check('Section 6 - Land - Eligible but not yet Patta given', eligNg, 'lowerIsBetter');
          check('Section 6 - Land - Applications not yet Scrutinised', notScr, 'lowerIsBetter');
          check('Section 6 - Land - Vacant Plots', vac, 'lowerIsBetter');
        } catch { /* silent */ }

        // Section 22 — FRA (LIVE)
        try {
          const fraKeys = [
            ['FRC_VERIFICATION_PENDING',   'Section 22 - FRA - FRC Verification Pending'],
            ['CLAIMS_PENDING_RESOLUTION',  'Section 22 - FRA - Claims pending for Resolution'],
            ['CLAIMS_PENDING_SDLC',        'Section 22 - FRA - Claims Pending with SDLC'],
            ['CLAIMS_PENDING_DLC',         'Section 22 - FRA - Claims Pending with DLC'],
            ['TITLES_PENDING',             'Section 22 - FRA - Titles Pending'],
          ];
          fraKeys.forEach(([field, label]) => {
            const map = {};
            Object.entries(fraByDistrict || {}).forEach(([d, row]) => {
              if (row?.[field] != null) map[d] = row[field];
            });
            check(label, map, 'lowerIsBetter');
          });
        } catch { /* silent */ }

        // All manual-entry pink metrics (across every section that has cross-district data)
        try {
          Object.entries(PINK_METRICS).forEach(([secKey, fields]) => {
            Object.entries(fields).forEach(([fieldKey, dir]) => {
              const map = manualDistrictMap(secKey, fieldKey);
              if (Object.keys(map).length < 3) return; // rankTier needs >=3 districts
              const label = `${SECTION_TITLE[secKey] || secKey} - ${FIELD_LABEL[fieldKey] || fieldKey}`;
              check(label, map, dir === 'higher' ? 'higherIsBetter' : 'lowerIsBetter');
            });
          });
        } catch { /* silent */ }

        if (weakMetrics.length === 0) return null;
        return (
          <div className="bg-rose-50 border border-rose-300 border-l-4 border-l-rose-600 p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div className="flex-1">
                <div className="text-sm font-bold text-rose-900 uppercase tracking-wide mb-2">
                  Needs Attention
                </div>
                <ul className="space-y-1">
                  {weakMetrics.map((m, i) => (
                    <li key={i} className="text-xs text-rose-900 flex items-start gap-2">
                      <span className="text-rose-600 font-bold mt-0.5">•</span>
                      <span><span className="font-semibold">{m.label}</span>{m.value != null && <span className="text-rose-700 ml-1">(current: {typeof m.value === 'number' ? m.value.toLocaleString('en-IN') : m.value})</span>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })()}

      {/* District Ranking summary — full page lives at /district-ranking. */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-4 py-3 bg-[#0f2e4d] text-white text-sm font-semibold uppercase tracking-wide flex items-center justify-between flex-wrap gap-2">
          <span>🏆 District Ranking</span>
          <a href="/district-ranking" className="text-[11px] font-normal normal-case text-blue-200 hover:text-white underline">
            View full ranking →
          </a>
        </div>
      </div>

      {/* Top header row — from KPI template top section (Static Data) — 2 rows, distinct soft tones */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {(() => {
          const h  = district ? (HABITATION_METRICS.districts[district] || {}) : HABITATION_METRICS.state;
          const sea = district ? (ST_HABITATION_SEA.districts[district] || {}) : ST_HABITATION_SEA.state;
          return (
            <>
              <HeaderCard label="No. of Blocks"                                       value={fmt(h.blocks)}                        tone="indigo"  onClick={() => setDrill({ kind: 'blocks' })} />
              <HeaderCard label="High Concentration SC Blocks (Top 3)"                value={h.top3_sc_blocks || NA}               tone="blue"    onClick={() => setDrill({ kind: 'top3_sc' })} />
              <HeaderCard label="High Concentration ST Blocks (Top 3)"                value={h.top3_st_blocks || NA}               tone="emerald" onClick={() => setDrill({ kind: 'top3_st' })} />
              <HeaderCard label="40%+ SC Population Villages"                         value={fmt(h.v40_sc_villages)}               tone="amber"   onClick={() => setDrill({ kind: 'v40_sc' })} />
              <HeaderCard label="40%+ ST Population Villages"                         value={fmt(h.v40_st_villages)}               tone="rose"    onClick={() => setDrill({ kind: 'v40_st' })} />
              <HeaderCard label="No. of SC Habitations"                               value={fmt(h.sc_habitations)}                tone="violet"  onClick={() => setDrill({ kind: 'sc_habitations' })} />
              <HeaderCard label="No. of ST Habitations"                               value={fmt(h.st_habitations)}                tone="teal"    onClick={() => setDrill({ kind: 'st_habitations' })} />
              <HeaderCard label="No. of ST Habitations (As per Socio-Economic Assessment)" value={fmt(sea.habitations)}          tone="slate"   onClick={() => setDrill({ kind: 'st_sea_habitations' })} />
            </>
          );
        })()}
      </div>

      {/* 1. Population — Category × (Nos / %) comparison table */}
      <Section num="1" title="Population" source="Auto-derived from source sheets">
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                <th className="px-4 py-2.5 w-1/3">Category</th>
                <th className="px-4 py-2.5 text-right">in Nos</th>
                <th className="px-4 py-2.5 text-right">in %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(() => {
                const h   = district ? (HABITATION_METRICS.districts[district] || {}) : HABITATION_METRICS.state;
                // ST population per Socio-Economic Assessment — file uses mixed-case
                // district keys, so match case-insensitively.
                const seaPopRow = (() => {
                  if (!district) return ST_SOCIO_ECON.state || {};
                  const key = Object.keys(ST_SOCIO_ECON.districts || {}).find(k => k.toUpperCase() === district.toUpperCase());
                  return key ? ST_SOCIO_ECON.districts[key] : {};
                })();
                const pop = h.population || {};
                const total = pop.total || 0;
                const stSeaPop = seaPopRow.st_population;
                const derived = {
                  general: { nos: pop.general ?? null, pct: total ? (pop.general / total) * 100 : null },
                  sc:      { nos: pop.sc      ?? null, pct: total ? (pop.sc / total) * 100      : null },
                  st:      { nos: pop.st      ?? null, pct: total ? (pop.st / total) * 100      : null },
                  st_sea:  { nos: stSeaPop ?? null,    pct: (total && stSeaPop != null) ? (stSeaPop / total) * 100 : null },
                };
                return [
                  { label: 'General',                                bg: 'bg-slate-50', k: 'general' },
                  { label: 'SC',                                     bg: 'bg-white',    k: 'sc' },
                  { label: 'ST',                                     bg: 'bg-slate-50', k: 'st' },
                  { label: 'ST (As per Socio-Economic Assessment)',  bg: 'bg-white',    k: 'st_sea' },
                ].map((row, i) => {
                  const overrideNos = mvFmt('sec1_population', `${row.k}_nos`);
                  const overridePct = mvFmt('sec1_population', `${row.k}_pct`);
                  const nos = overrideNos !== NA ? overrideNos : (derived[row.k].nos != null ? fmt(derived[row.k].nos) : NA);
                  const pctRaw = overridePct !== NA ? overridePct : (derived[row.k].pct != null ? `${derived[row.k].pct.toFixed(2)}%` : NA);
                  return (
                    <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                      <td className={`px-4 py-2.5 text-right ${nos === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{nos}</td>
                      <td className={`px-4 py-2.5 text-right ${pctRaw === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{pctRaw}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 2. Literacy rate — Category × (Male / Female / Total). Sourced from Census 2011 sheet. */}
      <Section num="2" title="Literacy rate (%)" source="Census 2011 (Static)" sectionKey="sec2_literacy" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {(() => {
          const lit = district ? (LITERACY_RATE.districts[district] || {}) : LITERACY_RATE.state;
          const pctFmt = (n) => (n === undefined || n === null) ? NA : `${Number(n).toFixed(2)}%`;
          const pick = (mkey, liveVal) => {
            const manual = mvFmt('sec2_literacy', mkey);
            if (manual !== NA) return manual;
            return pctFmt(liveVal);
          };
          const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
          const rows = [
            { label: 'Total (All Communities)', bg: 'bg-slate-50', k: 'total' },
            { label: 'SC',                      bg: 'bg-white',    k: 'sc'    },
            { label: 'ST',                      bg: 'bg-slate-50', k: 'st'    },
          ];
          return (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                    <th className="px-4 py-2.5 w-1/3">Category</th>
                    <th className="px-4 py-2.5 text-right">Male</th>
                    <th className="px-4 py-2.5 text-right">Female</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row, i) => {
                    const m = pick(`${row.k}_male`,   lit[`${row.k}_male`]);
                    const f = pick(`${row.k}_female`, lit[`${row.k}_female`]);
                    const t = pick(`${row.k}_total`,  lit[`${row.k}_total`]);
                    return (
                      <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(m)}`}>{m}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(f)}`}>{f}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(t)}`}>{t}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Section>

      {/* 3. Drop-out ratio — Category × (Boys / Girls / Total). Live-derived from the ADW/TW/Forest sheet. */}
      <Section num="3" title="Drop-outs (ADW / TW / Forest Schools) — 2025-26" source="EMIS (Static)" sectionKey="sec3_dropout" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {(() => {
          const dm = district ? (DROPOUT_METRICS.districts[district] || {}) : DROPOUT_METRICS.state;
          // Missing data here means "no dropouts recorded", not "unknown". Default to 0.
          const val = (mgmtKey, genderKey, override) => {
            if (override !== NA) return override;
            const bucket = dm[mgmtKey] || {};
            const v = bucket[genderKey];
            return fmt(v ?? 0);
          };
          return (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                    <th className="px-4 py-2.5 w-1/3">Category</th>
                    <th className="px-4 py-2.5 text-right">Boys</th>
                    <th className="px-4 py-2.5 text-right">Girls</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { label: 'Total',                    bg: 'bg-white',    k: 'total',  drill: 'all' },
                    { label: 'Adi-Dravida Welfare (ADW)',bg: 'bg-slate-50', k: 'adw',    drill: 'adw' },
                    { label: 'Tribal Welfare (TW)',      bg: 'bg-white',    k: 'tw',     drill: 'tw' },
                    { label: 'Forest Department',        bg: 'bg-slate-50', k: 'forest', drill: 'forest' },
                  ].map((row, i) => {
                    const b = val(row.k, 'boys',  mvFmt('sec3_dropout', `${row.k}_boys`));
                    const g = val(row.k, 'girls', mvFmt('sec3_dropout', `${row.k}_girls`));
                    const t = val(row.k, 'total', mvFmt('sec3_dropout', `${row.k}_total`));
                    const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                    return (
                      <tr key={i}
                          onClick={() => setDrill({ kind: 'dropouts', mgmt: row.drill, label: row.label })}
                          className={`${row.bg} hover:bg-blue-50 cursor-pointer transition-colors`}
                          title="Click to view student list">
                        <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(b)}`}>{b}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(g)}`}>{g}</td>
                        <td className={`px-4 py-2.5 text-right ${cls(t)}`}>{t}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Section>

      {/* 4. School Admission (ADW) — School KPI strip + Students + Teachers */}
      <Section num="4" title="School Admission (ADW)" source="EMIS (Static)" sectionKey="sec4_school_adw" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="space-y-4">
          {(() => {
            const s = district ? (ADW_SCHOOLS.districts[district] || {}) : ADW_SCHOOLS.state;
            const fmtN = (n) => fmt(n ?? 0);
            const kpis = [
              { label: 'Total no of Schools',              value: s.total_schools,   filter: 'all',     tone: 'slate' },
              { label: 'Total no of Schools running',      value: s.schools_running, filter: 'running', tone: 'emerald' },
              { label: 'Total no of Schools with zero strength', value: s.schools_zero, filter: 'zero',  tone: 'rose' },
            ];
            const toneCls = {
              slate:   'bg-slate-50 border-slate-200 text-slate-800',
              emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
              rose:    'bg-rose-50 border-rose-200 text-rose-800',
            };
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {kpis.map((k) => (
                  <div key={k.label}
                       onClick={() => setDrill({ kind: 'adw_schools', filter: k.filter, label: k.label })}
                       className={`border p-3 cursor-pointer hover:shadow-md transition-shadow ${toneCls[k.tone]}`}
                       title="Click to view school list">
                    <p className="text-[10px] uppercase tracking-wide font-semibold leading-tight opacity-80">{k.label}</p>
                    <p className="text-2xl font-bold mt-1">{fmtN(k.value)}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Students */}
          {(() => {
            const s = district ? (ADW_SCHOOLS.districts[district] || {}) : ADW_SCHOOLS.state;
            const r10 = district ? (SCHOOL_RESULTS['10_adw'].districts[district] || {}) : SCHOOL_RESULTS['10_adw'].state;
            const r12 = district ? (SCHOOL_RESULTS['12_adw'].districts[district] || {}) : SCHOOL_RESULTS['12_adw'].state;
            const fmtN = (n) => fmt(n ?? 0);
            const pctFmt = (n) => (n === undefined || n === null) ? NA : `${Number(n).toFixed(2)}%`;
            const derived = { boys: s.boys, girls: s.girls, total: s.total };
            // Ranking maps: higher pass % = better. Compute district → value once.
            const mkMap = (stream, key) => {
              const out = {};
              Object.entries(SCHOOL_RESULTS[stream].districts || {}).forEach(([d, v]) => {
                if (v && v[key] != null) out[d] = v[key];
              });
              return out;
            };
            const rankMaps = {
              boys:  { x: mkMap('10_adw', 'male_pass_pct'),   xii: mkMap('12_adw', 'male_pass_pct')   },
              girls: { x: mkMap('10_adw', 'female_pass_pct'), xii: mkMap('12_adw', 'female_pass_pct') },
              total: { x: mkMap('10_adw', 'pass_pct'),        xii: mkMap('12_adw', 'pass_pct')        },
            };
            return (
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5 w-1/4">Students</th>
                      <th className="px-4 py-2.5 text-right">in Nos (2026-27)</th>
                      <th className="px-4 py-2.5 text-right">Class X Pass % (2025-26)</th>
                      <th className="px-4 py-2.5 text-right">Class XII Pass % (2025-26)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { label: 'Boys',  bg: 'bg-slate-50', k: 'boys'  },
                      { label: 'Girls', bg: 'bg-white',    k: 'girls' },
                      { label: 'Total', bg: 'bg-slate-50', k: 'total' },
                    ].map((row, i) => {
                      const manualCount = mvFmt('sec4_school_adw', `${row.k}_count`);
                      const count = manualCount !== NA ? manualCount : fmtN(derived[row.k]);
                      // Pass % — gender-wise weighted from source sheet; falls back to manual override
                      let x   = mvFmt('sec4_school_adw', `${row.k}_x_pct`);
                      let xii = mvFmt('sec4_school_adw', `${row.k}_xii_pct`);
                      const pctForRow = (results, rowKey) =>
                        rowKey === 'boys'  ? results.male_pass_pct
                      : rowKey === 'girls' ? results.female_pass_pct
                      : rowKey === 'total' ? results.pass_pct
                      : null;
                      if (x === NA)   x = pctFmt(pctForRow(r10, row.k));
                      if (xii === NA) xii = pctFmt(pctForRow(r12, row.k));
                      const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                      // Rank tier for X and XII pass % cells (higher = better)
                      const xTier   = rankTier(rankMaps[row.k]?.x,   district, 'higherIsBetter');
                      const xiiTier = rankTier(rankMaps[row.k]?.xii, district, 'higherIsBetter');
                      return (
                        <tr key={i}
                            onClick={() => setDrill({ kind: 'adw_schools', filter: 'all', label: row.label })}
                            className={`${row.bg} hover:bg-blue-50 cursor-pointer transition-colors`}
                            title="Click to view school list">
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(count)}`}>{count}</td>
                          <td className={withRank(`px-4 py-2.5 text-right ${cls(x)}`, xTier)}
                              onClick={(e) => { if (row.k === 'total') { e.stopPropagation(); setDrill({ kind: 'school_results', stream: '10_adw', label: 'Class X Pass — ADW' }); } }}>
                            {x}
                          </td>
                          <td className={withRank(`px-4 py-2.5 text-right ${cls(xii)}`, xiiTier)}
                              onClick={(e) => { if (row.k === 'total') { e.stopPropagation(); setDrill({ kind: 'school_results', stream: '12_adw', label: 'Class XII Pass — ADW' }); } }}>
                            {xii}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Teachers */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                  <th className="px-4 py-2.5 w-1/4">Teachers</th>
                  <th className="px-4 py-2.5 text-right">Sanctioned</th>
                  <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                  <th className="px-4 py-2.5 text-right">Vacant in %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { label: 'Total',   bg: 'bg-white',    k: 'total' },
                  { label: 'General', bg: 'bg-slate-50', k: 'gen'   },
                  { label: 'SC',      bg: 'bg-white',    k: 'sc'    },
                  { label: 'ST',      bg: 'bg-slate-50', k: 'st'    },
                ].map((row, i) => {
                  const s = mvFmt('sec4_school_adw', `t_${row.k}_sanctioned`);
                  const a = mvFmt('sec4_school_adw', `t_${row.k}_actual`);
                  const v = mvFmt('sec4_school_adw', `t_${row.k}_vacant_pct`);
                  const cls = (x) => x === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                  // Vacant % is a "lower = better" ranking metric — but it's manual entry
                  // so we can only rank when values are saved for enough districts.
                  const vacantMap = manualDistrictMap('sec4_school_adw', `t_${row.k}_vacant_pct`);
                  const vTier = rankTier(vacantMap, district, 'lowerIsBetter');
                  return (
                    <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                      <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                      <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                      <td className={withRank(`px-4 py-2.5 text-right ${cls(v)}`, vTier)}>{v}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 4(a). School Admission (TW) — School KPI strip + Students + Teachers */}
      <Section num="4(a)" title="School Admission (TW)" source="EMIS (Static)" sectionKey="sec4a_school_tw" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="space-y-4">
          {(() => {
            const s = district ? (TW_SCHOOLS.districts[district] || {}) : TW_SCHOOLS.state;
            const fmtN = (n) => fmt(n ?? 0);
            const kpis = [
              { label: 'Total no of Schools',                    value: s.total_schools,   filter: 'all',     tone: 'slate' },
              { label: 'Total no of Schools running',            value: s.schools_running, filter: 'running', tone: 'emerald' },
              { label: 'Total no of Schools with zero strength', value: s.schools_zero,    filter: 'zero',    tone: 'rose' },
            ];
            const toneCls = {
              slate:   'bg-slate-50 border-slate-200 text-slate-800',
              emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
              rose:    'bg-rose-50 border-rose-200 text-rose-800',
            };
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {kpis.map((k) => (
                  <div key={k.label}
                       onClick={() => setDrill({ kind: 'tw_schools', filter: k.filter, label: k.label })}
                       className={`border p-3 cursor-pointer hover:shadow-md transition-shadow ${toneCls[k.tone]}`}
                       title="Click to view school list">
                    <p className="text-[10px] uppercase tracking-wide font-semibold leading-tight opacity-80">{k.label}</p>
                    <p className="text-2xl font-bold mt-1">{fmtN(k.value)}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Students */}
          {(() => {
            const s = district ? (TW_SCHOOLS.districts[district] || {}) : TW_SCHOOLS.state;
            const r10 = district ? (SCHOOL_RESULTS['10_tw'].districts[district] || {}) : SCHOOL_RESULTS['10_tw'].state;
            const r12 = district ? (SCHOOL_RESULTS['12_tw'].districts[district] || {}) : SCHOOL_RESULTS['12_tw'].state;
            const fmtN = (n) => fmt(n ?? 0);
            const pctFmt = (n) => (n === undefined || n === null) ? NA : `${Number(n).toFixed(2)}%`;
            const derived = { boys: s.boys, girls: s.girls, total: s.total };
            return (
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5 w-1/4">Students</th>
                      <th className="px-4 py-2.5 text-right">in Nos (2026-27)</th>
                      <th className="px-4 py-2.5 text-right">Class X Pass % (2025-26)</th>
                      <th className="px-4 py-2.5 text-right">Class XII Pass % (2025-26)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { label: 'Boys',  bg: 'bg-slate-50', k: 'boys'  },
                      { label: 'Girls', bg: 'bg-white',    k: 'girls' },
                      { label: 'Total', bg: 'bg-slate-50', k: 'total' },
                    ].map((row, i) => {
                      const manualCount = mvFmt('sec4a_school_tw', `${row.k}_count`);
                      const count = manualCount !== NA ? manualCount : fmtN(derived[row.k]);
                      let x   = mvFmt('sec4a_school_tw', `${row.k}_x_pct`);
                      let xii = mvFmt('sec4a_school_tw', `${row.k}_xii_pct`);
                      const pctForRow = (results, rowKey) =>
                        rowKey === 'boys'  ? results.male_pass_pct
                      : rowKey === 'girls' ? results.female_pass_pct
                      : rowKey === 'total' ? results.pass_pct
                      : null;
                      if (x === NA)   x = pctFmt(pctForRow(r10, row.k));
                      if (xii === NA) xii = pctFmt(pctForRow(r12, row.k));
                      const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                      // Rank tiers (higher pass % = better)
                      const twMkMap = (stream, key) => {
                        const out = {};
                        Object.entries(SCHOOL_RESULTS[stream].districts || {}).forEach(([d, v]) => {
                          if (v && v[key] != null) out[d] = v[key];
                        });
                        return out;
                      };
                      const twKey = row.k === 'boys' ? 'male_pass_pct' : row.k === 'girls' ? 'female_pass_pct' : 'pass_pct';
                      const xTier   = rankTier(twMkMap('10_tw', twKey), district, 'higherIsBetter');
                      const xiiTier = rankTier(twMkMap('12_tw', twKey), district, 'higherIsBetter');
                      return (
                        <tr key={i}
                            onClick={() => setDrill({ kind: 'tw_schools', filter: 'all', label: row.label })}
                            className={`${row.bg} hover:bg-blue-50 cursor-pointer transition-colors`}
                            title="Click to view school list">
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(count)}`}>{count}</td>
                          <td className={withRank(`px-4 py-2.5 text-right ${cls(x)}`, xTier)}
                              onClick={(e) => { if (row.k === 'total') { e.stopPropagation(); setDrill({ kind: 'school_results', stream: '10_tw', label: 'Class X Pass — TW' }); } }}>
                            {x}
                          </td>
                          <td className={withRank(`px-4 py-2.5 text-right ${cls(xii)}`, xiiTier)}
                              onClick={(e) => { if (row.k === 'total') { e.stopPropagation(); setDrill({ kind: 'school_results', stream: '12_tw', label: 'Class XII Pass — TW' }); } }}>
                            {xii}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Teachers */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                  <th className="px-4 py-2.5 w-1/4">Teachers</th>
                  <th className="px-4 py-2.5 text-right">Sanctioned</th>
                  <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                  <th className="px-4 py-2.5 text-right">Vacant in %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { label: 'Total',   bg: 'bg-white',    k: 'total' },
                  { label: 'General', bg: 'bg-slate-50', k: 'gen'   },
                  { label: 'SC',      bg: 'bg-white',    k: 'sc'    },
                  { label: 'ST',      bg: 'bg-slate-50', k: 'st'    },
                ].map((row, i) => {
                  const s = mvFmt('sec4a_school_tw', `t_${row.k}_sanctioned`);
                  const a = mvFmt('sec4a_school_tw', `t_${row.k}_actual`);
                  const v = mvFmt('sec4a_school_tw', `t_${row.k}_vacant_pct`);
                  const cls = (x) => x === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                  // Vacant % — lower = better
                  const vTier = rankTier(manualDistrictMap('sec4a_school_tw', `t_${row.k}_vacant_pct`), district, 'lowerIsBetter');
                  return (
                    <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                      <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                      <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                      <td className={withRank(`px-4 py-2.5 text-right ${cls(v)}`, vTier)}>{v}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 5. Hostel — ADW — LIVE from Nallosai ADW (KPI strip + Students + BioMetric); Wardens/Food/Inspection stay manual */}
      <Section num="5" title="Hostel — ADW" source="Nallosai ADW (LIVE)" sectionKey="sec5_hostel_adw" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {nallosaiAdwLoading && <p className="text-xs text-gray-500 mb-2">Loading Nallosai ADW data…</p>}
        {nallosaiAdwError   && <p className="text-xs text-red-700 mb-2">Nallosai ADW API error: {nallosaiAdwError} (falling back to manual entries)</p>}
        {(() => {
          const wiseById = {};
          nallosaiAdwHostelWise.forEach(w => { if (w.hostelId) wiseById[w.hostelId] = w; });
          const wantDist = (district || '').trim().toUpperCase();
          const detailsFiltered = nallosaiAdwHostelDetails.filter(h => {
            if (!wantDist) return true;
            const meta = wiseById[h.hostelId];
            return meta && nallosaiToDashDistrict(meta.district) === wantDist;
          });
          const wiseFiltered = nallosaiAdwHostelWise.filter(w => !wantDist || nallosaiToDashDistrict(w.district) === wantDist);
          const attFiltered = nallosaiAdwAttendance.filter(a => !wantDist || nallosaiToDashDistrict(a.district) === wantDist);
          const agg = (rows) => rows.reduce((acc, r) => {
            acc.sanctioned += (r.sanctionedStrength || 0);
            acc.actual     += (r.actualStudent     || 0);
            return acc;
          }, { sanctioned: 0, actual: 0 });
          const boys  = agg(detailsFiltered.filter(r => (r.genderType || '').toLowerCase().startsWith('m')));
          const girls = agg(detailsFiltered.filter(r => (r.genderType || '').toLowerCase().startsWith('f')));
          const total = { sanctioned: boys.sanctioned + girls.sanctioned, actual: boys.actual + girls.actual };
          const vacantPct = (o) => o.sanctioned ? Number((((o.sanctioned - o.actual) / o.sanctioned) * 100).toFixed(2)) : null;
          const totalHostels = detailsFiltered.length;
          const activeHostels   = detailsFiltered.filter(h => (h.actualStudent || 0) > 0).length;
          const inactiveHostels = totalHostels - activeHostels;
          const onlineHostels  = wiseFiltered.filter(w => (w.onlineStatus || '').toLowerCase() === 'online').length;
          const offlineHostels = wiseFiltered.filter(w => (w.onlineStatus || '').toLowerCase() === 'offline').length;
          const appsProcessed  = wiseFiltered.reduce((s, w) => s + (Number(w.applicationsProcessedByWarden) || 0), 0);
          const appsApproved   = wiseFiltered.reduce((s, w) => s + (Number(w.applicationsApprovedByDadwo)  || 0), 0);
          const attNumeric = attFiltered.map(a => Number(a.averageAttendancePercentage)).filter(Number.isFinite);
          const avgAttendance = attNumeric.length ? (attNumeric.reduce((a, b) => a + b, 0) / attNumeric.length) : null;
          const studentsBelow75 = attFiltered.reduce((sum, a) => sum + (Number(a.studentsBelow75) || 0), 0);
          const bottom5 = attFiltered.filter(a => a.poorPerformanceBottom5 === 'Yes').map(a => a.hostelName);
          const zeroAttCount = attFiltered.filter(a => a.zeroAttendance === 'Yes').length;
          const pickManual = (key, live) => {
            const m = mvFmt('sec5_hostel_adw', key);
            return m !== NA ? m : live;
          };
          const cls = (x) => x === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
          const studentRows = [
            { label: 'Total', bg: 'bg-white',    k: 'total', o: total },
            { label: 'Boys',  bg: 'bg-slate-50', k: 'boys',  o: boys  },
            { label: 'Girls', bg: 'bg-white',    k: 'girls', o: girls },
          ];
          const wardenRows = [
            { label: 'General', bg: 'bg-slate-50', k: 'gen'   },
            { label: 'SC',      bg: 'bg-white',    k: 'sc'    },
            { label: 'ST',      bg: 'bg-slate-50', k: 'st'    },
            { label: 'Total',   bg: 'bg-white',    k: 'total' },
          ];
          return (
            <div className="space-y-4">
              {/* KPI strip — Total / Active / Inactive Hostels */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: 'Total Hostels',    value: totalHostels,    tone: 'slate' },
                  { label: 'Active Hostels',   value: activeHostels,   tone: 'emerald' },
                  { label: 'Inactive Hostels', value: inactiveHostels, tone: 'rose' },
                ].map(k => {
                  const toneCls = {
                    slate:   'bg-slate-50 border-slate-200 text-slate-800',
                    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                    rose:    'bg-rose-50 border-rose-200 text-rose-800',
                  }[k.tone];
                  return (
                    <div key={k.label} className={`border p-3 ${toneCls}`}>
                      <p className="text-[10px] uppercase tracking-wide font-semibold leading-tight opacity-80">{k.label}</p>
                      <p className="text-2xl font-bold mt-1">{fmt(k.value)}</p>
                    </div>
                  );
                })}
              </div>

              {/* (i) Students & Wardens */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (i) Students &amp; Wardens
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5 w-1/4">Students</th>
                      <th className="px-4 py-2.5 text-right">Sanctioned</th>
                      <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                      <th className="px-4 py-2.5 text-right">Vacant in %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {studentRows.map((row, i) => {
                      const s = pickManual(`${row.k}_sanctioned`, fmt(row.o.sanctioned));
                      const a = pickManual(`${row.k}_actual`,     fmt(row.o.actual));
                      const vp = vacantPct(row.o);
                      const v = pickManual(`${row.k}_vacant_pct`, vp == null ? NA : `${vp}%`);
                      return (
                        <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(v)}`}>{v}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5">Wardens</th>
                      <th className="px-4 py-2.5 text-right">Sanctioned</th>
                      <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                      <th className="px-4 py-2.5 text-right">Vacant in %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {wardenRows.map((row, i) => {
                      const s = mvFmt('sec5_hostel_adw', `w_${row.k}_sanctioned`);
                      const a = mvFmt('sec5_hostel_adw', `w_${row.k}_actual`);
                      const v = mvFmt('sec5_hostel_adw', `w_${row.k}_vacant_pct`);
                      return (
                        <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(v)}`}>{v}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* (ii) BioMetric — split per sheet with filter row */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (ii) BioMetric
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Number of Online Devices</th>
                      <th className="px-4 py-2.5">Number of Offline Devices</th>
                      <th className="px-4 py-2.5">Applications Processed by Warden</th>
                      <th className="px-4 py-2.5">Applications Approved by DADWO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {(() => {
                        const cells = [
                          { k: 'bio_online_devices',  live: fmt(onlineHostels) },
                          { k: 'bio_offline_devices', live: fmt(offlineHostels) },
                          { k: 'bio_apps_processed',  live: fmt(appsProcessed) },
                          { k: 'bio_apps_approved',   live: fmt(appsApproved) },
                        ];
                        return cells.map((c, i) => {
                          const v = pickManual(c.k, c.live);
                          return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                        });
                      })()}
                    </tr>
                  </tbody>
                </table>
                <div className="px-4 py-1.5 bg-amber-50 border-y border-amber-200 text-[11px] text-amber-800 italic">
                  Filter 1 — From-To Date: applies to the attendance metrics below (currently last 7 days)
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Average Daily Attendance %</th>
                      <th className="px-4 py-2.5">Students with Attendance &lt; 75%</th>
                      <th className="px-4 py-2.5">Bottom-5 Hostels by Attendance</th>
                      <th className="px-4 py-2.5">Number of Hostels with Zero Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {(() => {
                        const cells = [
                          { k: 'bio_avg_attend_pct', live: avgAttendance == null ? NA : `${avgAttendance.toFixed(2)}%` },
                          { k: 'bio_below_75_pct',   live: studentsBelow75 == null ? NA : fmt(studentsBelow75) },
                          { k: 'bio_bottom5',        live: bottom5.length ? bottom5.slice(0, 5).join(', ') : NA },
                          { k: 'bio_zero_att_pct',   live: attFiltered.length ? fmt(zeroAttCount) : NA },
                        ];
                        return cells.map((c, i) => {
                          const v = pickManual(c.k, c.live);
                          return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                        });
                      })()}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* (iii) Food Quality — manual */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (iii) Food Quality
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Quantity Adequate for all Students?</th>
                      <th className="px-4 py-2.5">Veg / Eggs / Non-Veg per Norms?</th>
                      <th className="px-4 py-2.5">Major Source of Drinking Water</th>
                      <th className="px-4 py-2.5">Complaints in Last 6 Months?</th>
                      <th className="px-4 py-2.5">Complaints Resolved Immediately?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {['food_quantity_adequate','food_norms','food_water_source','food_complaints_6mo','food_complaints_resolved'].map((k, i) => {
                        const v = mvFmt('sec5_hostel_adw', k);
                        return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* (iv) Inspection — DADWO & ST columns per sheet */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (iv) Inspection
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Hostels to be Inspected by DADWO / Month (50% of Total)</th>
                      <th className="px-4 py-2.5">Inspections Conducted by DADWO</th>
                      <th className="px-4 py-2.5">DADWO Inspections Pending (Number &amp; %)</th>
                      <th className="px-4 py-2.5">Hostels to be Inspected by ST / Month (100% of Total)</th>
                      <th className="px-4 py-2.5">Inspections Conducted by ST</th>
                      <th className="px-4 py-2.5">ST Inspections Pending (Number &amp; %)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {['insp_dadwo_target','insp_dadwo_conducted','insp_dadwo_pending','insp_st_target','insp_st_conducted','insp_st_pending'].map((k, i) => {
                        const v = mvFmt('sec5_hostel_adw', k);
                        return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </Section>

      {/* 5(a). Hostel — TW — LIVE from Nallosai (Sanctioned/Actual/Vacant %, BioMetric attendance) */}
      <Section num="5(a)" title="Hostel — TW" source="Nallosai (LIVE)" sectionKey="sec5a_hostel_tw" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {nallosaiLoading && <p className="text-xs text-gray-500 mb-2">Loading Nallosai data…</p>}
        {nallosaiError   && <p className="text-xs text-red-700 mb-2">Nallosai API error: {nallosaiError} (falling back to manual entries)</p>}
        {(() => {
          const wiseById = {};
          nallosaiHostelWise.forEach(w => { if (w.hostelId) wiseById[w.hostelId] = w; });
          const wantDist = (district || '').trim().toUpperCase();
          const detailsFiltered = nallosaiHostelDetails.filter(h => {
            if (!wantDist) return true;
            const meta = wiseById[h.hostelId];
            return meta && nallosaiToDashDistrict(meta.district) === wantDist;
          });
          const wiseFiltered = nallosaiHostelWise.filter(w => !wantDist || nallosaiToDashDistrict(w.district) === wantDist);
          const attFiltered = nallosaiAttendance.filter(a => !wantDist || nallosaiToDashDistrict(a.district) === wantDist);
          const agg = (rows) => rows.reduce((acc, r) => {
            acc.sanctioned += (r.sanctionedStrength || 0);
            acc.actual     += (r.actualStudent     || 0);
            return acc;
          }, { sanctioned: 0, actual: 0 });
          const boys  = agg(detailsFiltered.filter(r => (r.genderType || '').toLowerCase().startsWith('m')));
          const girls = agg(detailsFiltered.filter(r => (r.genderType || '').toLowerCase().startsWith('f')));
          const total = { sanctioned: boys.sanctioned + girls.sanctioned, actual: boys.actual + girls.actual };
          const vacantPct = (o) => o.sanctioned ? Number((((o.sanctioned - o.actual) / o.sanctioned) * 100).toFixed(2)) : null;
          const totalHostels = detailsFiltered.length;
          const activeHostels   = detailsFiltered.filter(h => (h.actualStudent || 0) > 0).length;
          const inactiveHostels = totalHostels - activeHostels;
          // BioMetric — online/offline device counts from hostel-wise (onlineStatus)
          const onlineHostels  = wiseFiltered.filter(w => (w.onlineStatus || '').toLowerCase() === 'online').length;
          const offlineHostels = wiseFiltered.filter(w => (w.onlineStatus || '').toLowerCase() === 'offline').length;
          const appsProcessed  = wiseFiltered.reduce((s, w) => s + (Number(w.applicationsProcessedByWarden) || 0), 0);
          const appsApproved   = wiseFiltered.reduce((s, w) => s + (Number(w.applicationsApprovedByDadwo)  || 0), 0);
          // Attendance aggregates
          const attNumeric = attFiltered.map(a => Number(a.averageAttendancePercentage)).filter(Number.isFinite);
          const avgAttendance = attNumeric.length ? (attNumeric.reduce((a, b) => a + b, 0) / attNumeric.length) : null;
          const studentsBelow75 = attFiltered.reduce((sum, a) => sum + (Number(a.studentsBelow75) || 0), 0);
          const bottom5 = attFiltered.filter(a => a.poorPerformanceBottom5 === 'Yes').map(a => a.hostelName);
          const zeroAttCount = attFiltered.filter(a => a.zeroAttendance === 'Yes').length;
          // Helper: pick manual override first, else live value.
          const pickManual = (key, live) => {
            const m = mvFmt('sec5a_hostel_tw', key);
            return m !== NA ? m : live;
          };
          const cls = (x) => x === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';

          const studentRows = [
            { label: 'Total', bg: 'bg-white',    k: 'total', o: total },
            { label: 'Boys',  bg: 'bg-slate-50', k: 'boys',  o: boys  },
            { label: 'Girls', bg: 'bg-white',    k: 'girls', o: girls },
          ];
          const wardenRows = [
            { label: 'General', bg: 'bg-slate-50', k: 'gen'   },
            { label: 'SC',      bg: 'bg-white',    k: 'sc'    },
            { label: 'ST',      bg: 'bg-slate-50', k: 'st'    },
            { label: 'Total',   bg: 'bg-white',    k: 'total' },
          ];

          return (
            <div className="space-y-4">
              {/* KPI strip — Total / Active / Inactive Hostels */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: 'Total Hostels',    value: totalHostels,    tone: 'slate' },
                  { label: 'Active Hostels',   value: activeHostels,   tone: 'emerald' },
                  { label: 'Inactive Hostels', value: inactiveHostels, tone: 'rose' },
                ].map(k => {
                  const toneCls = {
                    slate:   'bg-slate-50 border-slate-200 text-slate-800',
                    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                    rose:    'bg-rose-50 border-rose-200 text-rose-800',
                  }[k.tone];
                  return (
                    <div key={k.label} className={`border p-3 ${toneCls}`}>
                      <p className="text-[10px] uppercase tracking-wide font-semibold leading-tight opacity-80">{k.label}</p>
                      <p className="text-2xl font-bold mt-1">{fmt(k.value)}</p>
                    </div>
                  );
                })}
              </div>

              {/* (i) Students & Wardens */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (i) Students &amp; Wardens
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5 w-1/4">Students</th>
                      <th className="px-4 py-2.5 text-right">Sanctioned</th>
                      <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                      <th className="px-4 py-2.5 text-right">Vacant in %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {studentRows.map((row, i) => {
                      const s = pickManual(`${row.k}_sanctioned`, fmt(row.o.sanctioned));
                      const a = pickManual(`${row.k}_actual`,     fmt(row.o.actual));
                      const vp = vacantPct(row.o);
                      const v = pickManual(`${row.k}_vacant_pct`, vp == null ? NA : `${vp}%`);
                      return (
                        <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(v)}`}>{v}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      <th className="px-4 py-2.5">Wardens</th>
                      <th className="px-4 py-2.5 text-right">Sanctioned</th>
                      <th className="px-4 py-2.5 text-right">Actual (as of now)</th>
                      <th className="px-4 py-2.5 text-right">Vacant in %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {wardenRows.map((row, i) => {
                      const s = mvFmt('sec5a_hostel_tw', `w_${row.k}_sanctioned`);
                      const a = mvFmt('sec5a_hostel_tw', `w_${row.k}_actual`);
                      const v = mvFmt('sec5a_hostel_tw', `w_${row.k}_vacant_pct`);
                      return (
                        <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(s)}`}>{s}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(a)}`}>{a}</td>
                          <td className={`px-4 py-2.5 text-right ${cls(v)}`}>{v}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* (ii) BioMetric — split into 2 tables per sheet, with filter row between */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (ii) BioMetric
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Number of Online Devices</th>
                      <th className="px-4 py-2.5">Number of Offline Devices</th>
                      <th className="px-4 py-2.5">Applications Processed by Warden</th>
                      <th className="px-4 py-2.5">Applications Approved by DADWO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {(() => {
                        const cells = [
                          { k: 'bio_online_devices',  live: fmt(onlineHostels) },
                          { k: 'bio_offline_devices', live: fmt(offlineHostels) },
                          { k: 'bio_apps_processed',  live: fmt(appsProcessed) },
                          { k: 'bio_apps_approved',   live: fmt(appsApproved) },
                        ];
                        return cells.map((c, i) => {
                          const v = pickManual(c.k, c.live);
                          return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                        });
                      })()}
                    </tr>
                  </tbody>
                </table>
                {/* Filter note (per sheet, only affects the row below) */}
                <div className="px-4 py-1.5 bg-amber-50 border-y border-amber-200 text-[11px] text-amber-800 italic">
                  Filter 1 — From-To Date: applies to the attendance metrics below (currently last 7 days)
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Average Daily Attendance %</th>
                      <th className="px-4 py-2.5">Students with Attendance &lt; 75%</th>
                      <th className="px-4 py-2.5">Bottom-5 Hostels by Attendance</th>
                      <th className="px-4 py-2.5">Number of Hostels with Zero Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {(() => {
                        const cells = [
                          { k: 'bio_avg_attend_pct', live: avgAttendance == null ? NA : `${avgAttendance.toFixed(2)}%` },
                          { k: 'bio_below_75_pct',   live: studentsBelow75 == null ? NA : fmt(studentsBelow75) },
                          { k: 'bio_bottom5',        live: bottom5.length ? bottom5.slice(0, 5).join(', ') : NA },
                          { k: 'bio_zero_att_pct',   live: attFiltered.length ? fmt(zeroAttCount) : NA },
                        ];
                        return cells.map((c, i) => {
                          const v = pickManual(c.k, c.live);
                          return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                        });
                      })()}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* (iii) Food Quality — manual */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (iii) Food Quality
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Quantity Adequate for all Students?</th>
                      <th className="px-4 py-2.5">Veg / Eggs / Non-Veg per Norms?</th>
                      <th className="px-4 py-2.5">Major Source of Drinking Water</th>
                      <th className="px-4 py-2.5">Complaints in Last 6 Months?</th>
                      <th className="px-4 py-2.5">Complaints Resolved Immediately?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {['food_quantity_adequate','food_norms','food_water_source','food_complaints_6mo','food_complaints_resolved'].map((k, i) => {
                        const v = mvFmt('sec5a_hostel_tw', k);
                        return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* (iv) Inspection — matches sheet: DADWO & ST inspection columns */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  (iv) Inspection
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Hostels to be Inspected by DADWO / Month (50% of Total)</th>
                      <th className="px-4 py-2.5">Inspections Conducted by DADWO</th>
                      <th className="px-4 py-2.5">DADWO Inspections Pending (Number &amp; %)</th>
                      <th className="px-4 py-2.5">Hostels to be Inspected by ST / Month (100% of Total)</th>
                      <th className="px-4 py-2.5">Inspections Conducted by ST</th>
                      <th className="px-4 py-2.5">ST Inspections Pending (Number &amp; %)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {['insp_dadwo_target','insp_dadwo_conducted','insp_dadwo_pending','insp_st_target','insp_st_conducted','insp_st_pending'].map((k, i) => {
                        const v = mvFmt('sec5a_hostel_tw', k);
                        return <td key={i} className={`px-4 py-2.5 ${cls(v)}`}>{v}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </Section>

      {/* 6. Distribution of Free House Site Pattas — 3 sub-tables (Land Portal live + Manual + Land Portal live) */}
      <Section num="6" title="Distribution of 'Free House Site Pattas'" source="Land Portal — LIVE" sectionKey="sec6_pattas_manual" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {landLoading && <p className="text-xs text-gray-500">Loading Land data...</p>}
        {landError && <p className="text-xs text-red-700">Land API error: {landError}</p>}

        <div className="space-y-4">
          {/* Sub-table 1: Land fields overview — LIVE Land Portal */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-emerald-100 border-b border-emerald-200 text-xs font-bold text-emerald-900 uppercase tracking-wide flex items-center justify-between gap-2">
              <span>Land Portal — Fields &amp; Conversion</span>
              <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Land Fields Acquired</th>
                  <th className="px-4 py-2.5">Total Extent in Acres</th>
                  <th className="px-4 py-2.5">Total Fields Surveyed</th>
                  <th className="px-4 py-2.5">Fields Yet to be Surveyed</th>
                  <th className="px-4 py-2.5">Land Converted to AD Natham</th>
                  <th className="px-4 py-2.5">Land Yet to Convert to AD Natham</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  {(() => {
                    // Drill-down: full Land Portal breakdown for the current scope
                    const openLandFhsDrill = (label) => {
                      const rows = [
                        { label: 'Land Fields Acquired',        value: fhs.land_fields_acquired },
                        { label: 'Total Extent (Acres)',        value: fhs.total_extent_in_acres },
                        { label: 'Total Fields Surveyed',       value: fhs.total_fields_surveyed },
                        { label: 'Fields Yet to be Surveyed',   value: fhs.total_fields_yet_to_survey },
                        { label: 'Land Converted to AD Natham', value: fhs.land_converted_to_ad_natham },
                        { label: 'Land Yet to Convert',         value: fhs.land_yet_to_convert },
                      ];
                      openDrill(`Land Portal — ${label}`, rows,
                        [{ key: 'label', label: 'Metric' }, { key: 'value', label: 'Value', render: r => fmt(r.value) }],
                        district ? `Land · ${district}` : 'Land · State');
                    };
                    const c = (v, lbl) => landData
                      ? { className: 'px-4 py-2.5 font-semibold text-gray-800 cursor-pointer hover:bg-blue-50 hover:underline', onClick: () => openLandFhsDrill(lbl), children: fmt(v) }
                      : { className: 'px-4 py-2.5 font-semibold text-gray-800', children: NA };
                    return (
                      <>
                        {[
                          [fhs.land_fields_acquired,       'Land Fields Acquired'],
                          [fhs.total_extent_in_acres,      'Total Extent (Acres)'],
                          [fhs.total_fields_surveyed,      'Total Fields Surveyed'],
                          [fhs.total_fields_yet_to_survey, 'Fields Yet to be Surveyed'],
                          [fhs.land_converted_to_ad_natham,'Land Converted to AD Natham'],
                          [fhs.land_yet_to_convert,        'Land Yet to Convert'],
                        ].map(([v, lbl], i) => {
                          const { className, onClick, children } = c(v, lbl);
                          return <td key={i} className={className} onClick={onClick}>{children}</td>;
                        })}
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sub-table 2: Pattas & applications — district-wise from static Excel; manual override still wins */}
          {(() => {
            const lp = district ? (LAND_PATTAS_DISTRICT.districts[district] || {}) : LAND_PATTAS_DISTRICT.state;
            const pick = (fieldKey) => {
              const manual = mvFmt('sec6_pattas_manual', fieldKey);
              if (manual !== NA) return manual;
              const v = lp[fieldKey];
              return (v === undefined || v === null) ? NA : fmt(v);
            };
            const cellCls = (v, rankClass = '') => `px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankClass}`;
            return (
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  Pattas &amp; Applications
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Pattas Can be Given in Acquired Fields</th>
                      <th className="px-4 py-2.5">Patta Given (No. of Beneficiaries)</th>
                      <th className="px-4 py-2.5">Vacant Plot</th>
                      <th className="px-4 py-2.5">Total Applications Received</th>
                      <th className="px-4 py-2.5">Eligible but Patta Not Given</th>
                      <th className="px-4 py-2.5">Rejected Petitions</th>
                      <th className="px-4 py-2.5">Applications Not Scrutinised</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {(() => { const v = pick('pattas_can_be_given');        return <td className={cellCls(v)}>{v}</td>; })()}
                      {(() => { const v = pick('pattas_given_beneficiaries'); return <td className={cellCls(v)}>{v}</td>; })()}
                      {(() => { const v = pick('vacant_plot');                return <td className={cellCls(v)}>{v}</td>; })()}
                      {(() => { const v = pick('total_applications');         return <td className={cellCls(v)}>{v}</td>; })()}
                      {(() => { const v = pick('eligible_not_given');         return <td className={cellCls(v, rankedManual('sec6_pattas_manual','eligible_not_given'))}>{v}</td>; })()}
                      {(() => { const v = pick('rejected_petitions');         return <td className={cellCls(v, rankedManual('sec6_pattas_manual','rejected_petitions'))}>{v}</td>; })()}
                      {(() => { const v = pick('not_scrutinised');            return <td className={cellCls(v, rankedManual('sec6_pattas_manual','not_scrutinised'))}>{v}</td>; })()}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Sub-table 3: e-Patta & Court Cases — LIVE Land Portal */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-emerald-100 border-b border-emerald-200 text-xs font-bold text-emerald-900 uppercase tracking-wide flex items-center justify-between gap-2">
              <span>Land Portal — e-Patta &amp; Court Cases</span>
              <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Number of e-Pattas Issued</th>
                  <th className="px-4 py-2.5">Number of e-Pattas Yet to be Issued</th>
                  <th className="px-4 py-2.5">Number of e-Patta Cannot be Given</th>
                  <th className="px-4 py-2.5">Fields under Court Cases (except LAOP)</th>
                  <th className="px-4 py-2.5">Plots under Court Cases</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  {(() => {
                    const openEpDrill = (label) => {
                      const rows = [
                        { label: 'e-Pattas Issued',              value: ep.issued },
                        { label: 'e-Pattas Yet to be Issued',    value: ep.yet_to_be_issued },
                        { label: 'e-Patta Cannot be Given',      value: ep.cannot_be_given },
                        { label: 'Fields under Court Cases',     value: cc.fields_under_court_cases },
                        { label: 'Plots under Court Cases',      value: cc.plots_under_court_cases },
                      ];
                      openDrill(`Land Portal — ${label}`, rows,
                        [{ key: 'label', label: 'Metric' }, { key: 'value', label: 'Value', render: r => fmt(r.value) }],
                        district ? `Land · ${district}` : 'Land · State');
                    };
                    const c = (v, lbl) => landData
                      ? { className: 'px-4 py-2.5 font-semibold text-gray-800 cursor-pointer hover:bg-blue-50 hover:underline', onClick: () => openEpDrill(lbl), children: fmt(v) }
                      : { className: 'px-4 py-2.5 font-semibold text-gray-800', children: NA };
                    return (
                      <>
                        {[
                          [ep.issued,                    'e-Pattas Issued'],
                          [ep.yet_to_be_issued,          'e-Pattas Yet to be Issued'],
                          [ep.cannot_be_given,           'e-Patta Cannot be Given'],
                          [cc.fields_under_court_cases,  'Fields under Court Cases'],
                          [cc.plots_under_court_cases,   'Plots under Court Cases'],
                        ].map(([v, lbl], i) => {
                          const { className, onClick, children } = c(v, lbl);
                          return <td key={i} className={className} onClick={onClick}>{children}</td>;
                        })}
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 7. Court Cases — single row comparison, Manual Entry */}
      <Section num="7" title="Court Cases" source="Manual Entry" sectionKey="sec7_court_cases" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Pending</th>
                <th className="px-4 py-2.5">New Cases (from 01.01.2026)</th>
                <th className="px-4 py-2.5">More than 1 Year Pending</th>
                <th className="px-4 py-2.5">Contempt Cases (if any)</th>
                <th className="px-4 py-2.5">No. of CA Not Filed</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec7_court_cases', 'pending');        return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec7_court_cases','pending')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec7_court_cases', 'new_since_2026'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec7_court_cases', 'more_than_1yr');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec7_court_cases','more_than_1yr')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec7_court_cases', 'contempt');       return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec7_court_cases','contempt')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec7_court_cases', 'ca_not_filed');   return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec7_court_cases','ca_not_filed')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 8. Number of CM Cell Petitions — single row, Manual Entry (TNeGA link) */}
      <Section num="8" title="Number of CM Cell Petitions" source="Manual Entry (via TNeGA)" sectionKey="sec8_cm_cell" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Total Pending</th>
                <th className="px-4 py-2.5">New Cases Received (from new govt took charge)</th>
                <th className="px-4 py-2.5">Current Petitions — Rating A</th>
                <th className="px-4 py-2.5">Current Petitions — Rating B</th>
                <th className="px-4 py-2.5">Current Petitions — Rating C</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec8_cm_cell', 'pending');   return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec8_cm_cell','pending')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec8_cm_cell', 'new_cases'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec8_cm_cell', 'rating_a');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec8_cm_cell', 'rating_b');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec8_cm_cell','rating_b')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec8_cm_cell', 'rating_c');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec8_cm_cell','rating_c')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 9. RTI — single row, Manual Entry */}
      <Section num="9" title="RTI" source="Manual Entry" sectionKey="sec9_rti" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Total Application Received (from 01.01.2026)</th>
                <th className="px-4 py-2.5">No. of Disposal Applications</th>
                <th className="px-4 py-2.5">Pending</th>
                <th className="px-4 py-2.5">No. of RTI 1st Appeal</th>
                <th className="px-4 py-2.5">No. of RTI Appeal (2nd level) in Commission</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec9_rti', 'received');          return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec9_rti', 'disposed');          return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec9_rti', 'pending');           return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec9_rti','pending')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec9_rti', 'appeal1');           return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec9_rti','appeal1')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec9_rti', 'appeal_commission'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec9_rti','appeal_commission')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 10. Pending files at Government — single row, Manual Entry */}
      <Section num="10" title="Pending files at Government" source="Manual Entry" sectionKey="sec10_pending_files" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Total Pending</th>
                <th className="px-4 py-2.5">More than 1 yr</th>
                <th className="px-4 py-2.5">More than 3 yrs</th>
                <th className="px-4 py-2.5">More than 5 yrs</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec10_pending_files', 'total');    return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec10_pending_files','total')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec10_pending_files', 'more_1yr'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec10_pending_files','more_1yr')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec10_pending_files', 'more_3yr'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec10_pending_files','more_3yr')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec10_pending_files', 'more_5yr'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec10_pending_files','more_5yr')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 11.1 Burial grounds — SC — single row */}
      <Section num="11.1" title="Burial grounds / Crematorium — SC" source="Manual Entry" sectionKey="sec11_1_burial_sc" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Total SC Habitations</th>
                <th className="px-4 py-2.5">SC Habitations with Common Burial Ground (without issues)</th>
                <th className="px-4 py-2.5">SC Habitations with Dedicated Burial Grounds</th>
                <th className="px-4 py-2.5">SC Habitations without Dedicated Burial Grounds</th>
                <th className="px-4 py-2.5">SC Habitations without Pathways</th>
                <th className="px-4 py-2.5">Encroachments on Burial Ground Land / Path Access (SC)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'total_sc_hab');      return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'common_no_issues'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'dedicated');         return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'without_dedicated'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_1_burial_sc','without_dedicated')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'without_pathways');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_1_burial_sc','without_pathways')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_1_burial_sc', 'encroachments');     return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_1_burial_sc','encroachments')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 11.2 Burial grounds — ST — single row */}
      <Section num="11.2" title="Burial grounds / Crematorium — ST" source="Manual Entry" sectionKey="sec11_2_burial_st" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Total ST Habitations</th>
                <th className="px-4 py-2.5">ST Habitations with Common Burial Ground (without issues)</th>
                <th className="px-4 py-2.5">ST Habitations with Dedicated Burial Grounds</th>
                <th className="px-4 py-2.5">ST Habitations without Dedicated Burial Grounds</th>
                <th className="px-4 py-2.5">ST Habitations without Pathways</th>
                <th className="px-4 py-2.5">Encroachments on Burial Ground Land / Path Access</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec11_2_burial_st', 'total_st_hab');     return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_2_burial_st', 'common_no_issues'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_2_burial_st', 'dedicated');         return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_2_burial_st', 'without_dedicated'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_2_burial_st','without_dedicated')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_2_burial_st', 'without_pathways');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_2_burial_st','without_pathways')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec11_2_burial_st', 'encroachments');     return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec11_2_burial_st','encroachments')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 12. Transport Access / Water — single row */}
      <Section num="12" title="Transport Access / Water" source="Manual Entry (Numerical)" sectionKey="sec12_transport_water" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Habitations without Public Transport</th>
                <th className="px-4 py-2.5">Habitations without All-Weather Roads</th>
                <th className="px-4 py-2.5">Habitations without Drinking Water</th>
                <th className="px-4 py-2.5">Habitations without Mobile Network</th>
                <th className="px-4 py-2.5">Habitations without Electricity</th>
                <th className="px-4 py-2.5">ST Households without Electricity</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec12_transport_water', 'no_public_transport');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_public_transport')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec12_transport_water', 'no_all_weather');       return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_all_weather')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec12_transport_water', 'no_drinking_water');    return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_drinking_water')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec12_transport_water', 'no_mobile');            return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_mobile')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec12_transport_water', 'no_electricity_hab');   return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_electricity_hab')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec12_transport_water', 'no_electricity_st_hh'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec12_transport_water','no_electricity_st_hh')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 14. Buildings (School/Hostel) — single row */}
      <Section num="13" title="Buildings (School/Hostel) conditions (in nos)" source="Manual Entry" sectionKey="sec14_buildings" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Between 20 to 30 yrs</th>
                <th className="px-4 py-2.5">Between 30 to 40 yrs</th>
                <th className="px-4 py-2.5">More than 40 yrs</th>
                <th className="px-4 py-2.5">Applied Stability Certificate for aged buildings?</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec14_buildings', 'y20_30');         return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec14_buildings','y20_30')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec14_buildings', 'y30_40');         return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec14_buildings','y30_40')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec14_buildings', 'y40_plus');       return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec14_buildings','y40_plus')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec14_buildings', 'stability_cert'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec14_buildings','stability_cert')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 17. DAPSC/ST — single row (Manual + DAP Portal columns) */}
      <Section num="14" title="DAPSC/ST — District Monitoring Committee" source="Manual + DAP Portal" sectionKey="sec17_dap" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Was District Monitoring Committee formed?</th>
                <th className="px-4 py-2.5">DMC Meeting Status — Quarter 1 (Conducted / Not Conducted)</th>
                <th className="px-4 py-2.5">DMC Meeting Status — Quarter 2 (Conducted / Not Conducted)</th>
                <th className="px-4 py-2.5">DMC Meeting Status — Quarter 3 (Conducted / Not Conducted)</th>
                <th className="px-4 py-2.5">DMC Meeting Status — Quarter 4 (Conducted / Not Conducted)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec17_dap', 'dmc_formed'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec17_dap', 'q1'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec17_dap', 'q2'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec17_dap', 'q3'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec17_dap', 'q4'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 21. Community Certificate — single row, Manual Entry */}
      <Section num="15" title="Community Certificate" source="Manual Entry" sectionKey="sec21_community_cert" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">How many applications are Pending?</th>
                <th className="px-4 py-2.5">Pending with DSP</th>
                <th className="px-4 py-2.5">Pending with Anthropology</th>
                <th className="px-4 py-2.5">Pending with RDO</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec21_community_cert', 'total_pending');  return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec21_community_cert','total_pending')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec21_community_cert', 'pending_dsp');    return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec21_community_cert','pending_dsp')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec21_community_cert', 'pending_anthro'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec21_community_cert','pending_anthro')}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec21_community_cert', 'pending_rdo');    return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedManual('sec21_community_cert','pending_rdo')}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 19. District Adi Dravidar Welfare Committee — single row */}
      <Section num="16" title="District Adi Dravidar Welfare Committee" source="Manual Entry" sectionKey="sec19_dw_committee" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">How many meetings held during last year?</th>
                <th className="px-4 py-2.5">When was the last meeting conducted?</th>
                <th className="px-4 py-2.5">Petitions received during 2025-26?</th>
                <th className="px-4 py-2.5">Petitions yet to be resolved during 2025-26?</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec19_dw_committee', 'meetings_held'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec19_dw_committee', 'last_meeting_date'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec19_dw_committee', 'petitions_received'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec19_dw_committee', 'petitions_unresolved'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 17. Cleanliness Committee — single row */}
      <Section num="17" title="Cleanliness Committee" source="Manual + Cleanliness Board Portal" sectionKey="sec20_cleanliness" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">How many meetings held during last year?</th>
                <th className="px-4 py-2.5">When was the last meeting conducted?</th>
                <th className="px-4 py-2.5">How many members enrolled in 2025-26?</th>
                <th className="px-4 py-2.5">How many members enrolled in 2026-27?</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                {(() => { const v = mvFmt('sec20_cleanliness', 'meetings_held'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec20_cleanliness', 'last_meeting_date'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec20_cleanliness', 'members_2025_26'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
                {(() => { const v = mvFmt('sec20_cleanliness', 'members_2026_27'); return <td className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'}`}>{v}</td>; })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 18. Vigilance Monitoring Committees — DLVMC & SDLVMC quarter status (LIVE) */}
      <Section num="18" title="Vigilance Monitoring Committees (DLVMC & SDLVMC)" source="POA Portal — LIVE">
        {poaLoading && <p className="text-xs text-gray-500">Loading POA data...</p>}
        {poaError && <p className="text-xs text-red-700">POA API error: {poaError}</p>}

        {poaData && (() => {
          const dlvmc  = vmcMtg.dlvmc  || {};
          const sdlvmc = vmcMtg.sdlvmc || {};
          const dlQ    = dlvmc.currentYearQuarters  || {};
          const sdQ    = sdlvmc.currentYearQuarters || {};
          const currentYearLabel = vmcMtg.currentYear || '';
          const statusPill = (s) => {
            const cls = s === 'Conducted'     ? 'bg-green-100 text-green-700 border-green-200'
                     : s === 'Not Conducted'  ? 'bg-red-100 text-red-700 border-red-200'
                     :                          'bg-gray-100 text-gray-600 border-gray-200';
            return <span className={`text-[10px] font-semibold border px-1.5 py-0.5 ${cls}`}>{s || 'NA'}</span>;
          };
          const qCell = (q) => {
            // roll-up rows use `display` ("24/38"); subdivision-wise rows use `count`
            const value = q?.display ?? (q?.count != null ? String(q.count) : null) ?? NA;
            return (
              <td className="px-4 py-2.5">
                <div className="font-semibold text-gray-800">{value}</div>
                {statusPill(q?.status)}
              </td>
            );
          };

          return (
            <div className="space-y-4">
              {/* A. DLVMC — Q1..Q4 (Conducted / Not Conducted / NA) */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
                  <span>A. DLVMC — {currentYearLabel} (Quarter-wise)</span>
                  <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
                </div>
                <div className="px-4 py-2 bg-slate-50 border-b border-gray-200 text-[11px] text-slate-700 flex flex-wrap gap-4">
                  <span><span className="font-semibold">Meetings conducted last year ({dlvmc?.previousYear ? (vmcMtg.previousYear || 'prev') : 'prev'}):</span> {dlvmc?.previousYear?.display || NA}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Q1</th>
                      <th className="px-4 py-2.5">Q2</th>
                      <th className="px-4 py-2.5">Q3</th>
                      <th className="px-4 py-2.5">Q4</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50">
                      {['Q1','Q2','Q3','Q4'].map(q => {
                        const cell = dlQ[q];
                        const clickable = cell != null;
                        return (
                          <td key={q} className={`px-4 py-2.5 ${clickable ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                              onClick={clickable ? () => openDrill(
                                `DLVMC ${q} — ${currentYearLabel}`,
                                [{ scope: 'DLVMC', quarter: q, ...cell }],
                                [
                                  { key: 'scope', label: 'Committee' },
                                  { key: 'quarter', label: 'Quarter' },
                                  { key: 'display', label: 'Completed / Expected' },
                                  { key: 'completed', label: 'Completed' },
                                  { key: 'expected', label: 'Expected' },
                                  { key: 'status', label: 'Status' },
                                ],
                                district ? `POA · ${district}` : 'POA · State'
                              ) : undefined}>
                            <div className="font-semibold text-gray-800">{cell?.display || NA}</div>
                            {statusPill(cell?.status)}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* B. SDLVMC — one row per subdivision when API returns breakdown
                  (a district is selected); state view shows the roll-up. */}
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
                  <span>B. SDLVMC — {currentYearLabel} (Quarter-wise)</span>
                  <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
                </div>
                <div className="px-4 py-2 bg-slate-50 border-b border-gray-200 text-[11px] text-slate-700 flex flex-wrap gap-4">
                  <span><span className="font-semibold">Meetings conducted last year:</span> {sdlvmc?.previousYear?.display || NA}</span>
                  <span><span className="font-semibold">Total Sub-divisions:</span> {fmt(sdlvmc?.totalSubdivisions)}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                      <th className="px-4 py-2.5">Subdivision</th>
                      <th className="px-4 py-2.5">Q1</th>
                      <th className="px-4 py-2.5">Q2</th>
                      <th className="px-4 py-2.5">Q3</th>
                      <th className="px-4 py-2.5">Q4</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sdlvmcClickCell = (rowLabel, q, cell) => {
                        const clickable = cell != null;
                        return (
                          <td key={q} className={`px-4 py-2.5 ${clickable ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                              onClick={clickable ? () => openDrill(
                                `SDLVMC ${q} — ${rowLabel}`,
                                [{ subdivision: rowLabel, quarter: q, ...cell }],
                                [
                                  { key: 'subdivision', label: 'Subdivision' },
                                  { key: 'quarter', label: 'Quarter' },
                                  { key: 'count', label: 'Count' },
                                  { key: 'display', label: 'Completed / Expected' },
                                  { key: 'status', label: 'Status' },
                                ],
                                district ? `POA · ${district}` : 'POA · State'
                              ) : undefined}>
                            <div className="font-semibold text-gray-800">{cell?.display ?? (cell?.count != null ? String(cell.count) : NA)}</div>
                            {statusPill(cell?.status)}
                          </td>
                        );
                      };
                      if (Array.isArray(sdlvmc.subdivisionWise) && sdlvmc.subdivisionWise.length > 0) {
                        return sdlvmc.subdivisionWise.map((sub, i) => {
                          const nm = sub.subdivision || `Subdivision ${i + 1}`;
                          return (
                            <tr key={i} className={i % 2 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-4 py-2.5 font-medium text-gray-800">{nm}</td>
                              {sdlvmcClickCell(nm, 'Q1', sub.Q1)}
                              {sdlvmcClickCell(nm, 'Q2', sub.Q2)}
                              {sdlvmcClickCell(nm, 'Q3', sub.Q3)}
                              {sdlvmcClickCell(nm, 'Q4', sub.Q4)}
                            </tr>
                          );
                        });
                      }
                      return (
                        <tr className="bg-slate-50">
                          <td className="px-4 py-2.5 font-medium text-gray-600 italic">
                            All Subdivisions ({fmt(sdlvmc.totalSubdivisions)})
                          </td>
                          {sdlvmcClickCell('All Subdivisions', 'Q1', sdQ.Q1)}
                          {sdlvmcClickCell('All Subdivisions', 'Q2', sdQ.Q2)}
                          {sdlvmcClickCell('All Subdivisions', 'Q3', sdQ.Q3)}
                          {sdlvmcClickCell('All Subdivisions', 'Q4', sdQ.Q4)}
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
                {(!Array.isArray(sdlvmc.subdivisionWise) || sdlvmc.subdivisionWise.length === 0) && (
                  <div className="px-4 py-1.5 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800 italic">
                    Per-subdivision breakdown not available for the state view — select a district to see subdivision names.
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Section>

      {/* 18a. Atrocity-prone / Awareness programmes — manual entry */}
      <Section num="18a" title="Atrocity-Prone Villages & Awareness Programmes" source="Manual Entry" sectionKey="sec18_vmc_manual" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                <th className="px-4 py-2.5">Have atrocity-prone villages/areas been identified &amp; reviewed?</th>
                <th className="px-4 py-2.5">Have awareness programmes been conducted in vulnerable villages?</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                <td className={`px-4 py-2.5 font-semibold ${mvFmt('sec18_vmc_manual', 'atrocity_prone_reviewed') === NA ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                  {mvFmt('sec18_vmc_manual', 'atrocity_prone_reviewed')}
                </td>
                <td className={`px-4 py-2.5 font-semibold ${mvFmt('sec18_vmc_manual', 'awareness_programmes') === NA ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                  {mvFmt('sec18_vmc_manual', 'awareness_programmes')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* POA Filter bar — sits directly above Section 19. All filters optional; empty = totals. */}
      <div className="bg-slate-50 border border-slate-200 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-800 uppercase tracking-wide">
          <ShieldAlert className="w-4 h-4 text-rose-700" /> POA Claim Filters
          <span className="text-[10px] font-normal text-slate-500 lowercase tracking-normal normal-case">— optional. Leave blank for state/district totals.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Filter 1 — Date of Registration From</label>
            <input type="date" value={registrationFrom} onChange={(e) => setRegistrationFrom(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Filter 1 — Date of Registration To</label>
            <input type="date" value={registrationTo} onChange={(e) => setRegistrationTo(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Filter 2 — Relief Disbursement From</label>
            <input type="date" value={disbursementFrom} onChange={(e) => setDisbursementFrom(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Filter 2 — Relief Disbursement To</label>
            <input type="date" value={disbursementTo} onChange={(e) => setDisbursementTo(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm bg-white" />
          </div>
        </div>
        {(registrationFrom || registrationTo || disbursementFrom || disbursementTo) && (
          <div className="flex items-center justify-end">
            <button
              onClick={() => { setRegistrationFrom(''); setRegistrationTo(''); setDisbursementFrom(''); setDisbursementTo(''); }}
              className="text-xs text-rose-700 hover:text-rose-900 font-semibold flex items-center gap-1"
              title="Clear all POA filters — reverts to totals"
            >
              <X className="w-3.5 h-3.5" /> Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* 19. POA Claims — 2 sub-tables (Cases + Monetary Relief), LIVE POA Portal */}
      <Section num="19" title="POA Claims" source="POA Portal — LIVE">
        {poaLoading && <p className="text-xs text-gray-500">Loading POA data...</p>}
        {poaError && <p className="text-xs text-red-700">POA API error: {poaError}</p>}

        <div className="space-y-4">
          {/* Sub-table 1: Cases */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
              <span>Cases</span>
              <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Cases Registered</th>
                  <th className="px-4 py-2.5">MF Category Cases</th>
                  <th className="px-4 py-2.5">Cases Pending in UI Stage</th>
                  <th className="px-4 py-2.5">Cases Pending in PT Stage</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  {(() => {
                    const casesBreakup = () => {
                      const rows = [
                        { label: 'Total Reported Cases',          value: cs.Total_Reported_Cases },
                        { label: 'Cases Pending — UI Stage',      value: cs.Total_UI_Cases },
                        { label: 'Cases Pending — PT Stage',      value: cs.Total_PT_Cases },
                        { label: 'Chargesheeted Cases',           value: cs.Total_Chargesheeted_Cases },
                        { label: 'Referred Chargesheeted Cases',  value: cs.Total_Reffered_Chargesheeted_Cases },
                        { label: 'FIR — Current Year',            value: cs.Total_FIR_currentyear },
                        { label: 'Judgements',                    value: cs.Total_Judgements },
                        { label: 'Acquitted',                     value: cs.Total_Acquitted },
                        { label: 'Convicted',                     value: cs.Total_Convicted },
                        { label: 'MF Category Cases',             value: rs.mf_cases },
                      ];
                      openDrill('POA — Case Summary Breakdown', rows,
                        [{ key: 'label', label: 'Metric' }, { key: 'value', label: 'Count', render: r => fmt(r.value) }],
                        district ? `POA · ${district}` : 'POA · State');
                    };
                    const reliefBreakup = () => {
                      const rows = [
                        { label: 'FIR — Proposal sent to DC',   value: rs.fir_proposal_sent_to_dc },
                        { label: 'FIR — Relief given',          value: rs.fir_relief_given },
                        { label: 'FIR — Relief pending',        value: rs.fir_relief_pending },
                        { label: 'Chargesheet — Proposal sent', value: rs.chargesheet_proposal_sent_to_dc },
                        { label: 'Chargesheet — Relief given',  value: rs.chargesheet_relief_given },
                        { label: 'Chargesheet — Relief pending',value: rs.chargesheet_relief_pending },
                        { label: 'Trial — Proposal sent',       value: rs.trial_proposal_sent_to_dc },
                        { label: 'Trial — Relief given',        value: rs.trial_relief_given },
                        { label: 'Trial — Relief pending',      value: rs.trial_relief_pending },
                        { label: 'Total cases',                 value: rs.total_cases },
                        { label: 'Non-MF cases',                value: rs.nonmf_case },
                      ];
                      openDrill('POA — Relief Summary Breakdown', rows,
                        [{ key: 'label', label: 'Metric' }, { key: 'value', label: 'Count', render: r => fmt(r.value) }],
                        district ? `POA · ${district}` : 'POA · State');
                    };
                    const cellCls = 'px-4 py-2.5 font-semibold text-gray-800 cursor-pointer hover:bg-blue-50 hover:underline';
                    return (
                      <>
                        <td className={cellCls} onClick={casesBreakup}>{poaData ? fmt(claims.casesRegistered ?? cs.Total_Reported_Cases) : NA}</td>
                        <td className={cellCls} onClick={reliefBreakup}>{poaData ? fmt(claims.mfCategoryCases ?? rs.mf_cases) : NA}</td>
                        <td className={cellCls} onClick={casesBreakup}>{poaData ? fmt(claims.uiPending ?? cs.Total_UI_Cases) : NA}</td>
                        <td className={cellCls} onClick={casesBreakup}>{poaData ? fmt(claims.ptPending ?? cs.Total_PT_Cases) : NA}</td>
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sub-table 2: Monetary Relief — Stage pending + amount spent */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
              <span>Monetary Relief</span>
              <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Cases Pending — Stage 1 (FIR)</th>
                  <th className="px-4 py-2.5">Cases Pending — Stage 2 (Chargesheet)</th>
                  <th className="px-4 py-2.5">Cases Pending — Stage 3 (Trial)</th>
                  <th className="px-4 py-2.5">Amount Spent for Monetary Relief (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(money.stage1Pending ?? rs.fir_relief_pending) : NA}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(money.stage2Pending ?? rs.chargesheet_relief_pending) : NA}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(money.stage3Pending ?? rs.trial_relief_pending) : NA}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmtInr(money.amountSpent) : NA}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sub-table 2b: Amount Breakup */}
          {poaData && money.amountBreakup && (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
                <span>Amount Spent — Stage-wise Breakup (₹)</span>
                <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                    <th className="px-4 py-2.5">FIR Stage</th>
                    <th className="px-4 py-2.5">Chargesheet Stage</th>
                    <th className="px-4 py-2.5">Final Stage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2.5 font-semibold text-gray-800">{fmtInr(money.amountBreakup.firStage)}</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-800">{fmtInr(money.amountBreakup.chargesheetStage)}</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-800">{fmtInr(money.amountBreakup.finalStage)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Sub-table 3: Additional Relief */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-rose-100 border-b border-rose-200 text-xs font-bold text-rose-900 uppercase tracking-wide flex items-center justify-between gap-2">
              <span>Additional Relief — Pending</span>
              <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 font-semibold">LIVE</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Employment (Job) Pending</th>
                  <th className="px-4 py-2.5">Pension Pending</th>
                  <th className="px-4 py-2.5">Patta Pending</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(addRel.employmentPending) : NA}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(addRel.pensionPending) : NA}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{poaData ? fmt(addRel.pattaPending) : NA}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Super-admin debug — raw POA payload, useful when live values don't match expectations */}
          {isSuperAdmin && poaData && (
            <details className="text-xs bg-slate-50 border border-slate-200 p-2">
              <summary className="cursor-pointer text-slate-600 font-semibold">Raw POA API response (debug)</summary>
              <pre className="mt-2 overflow-auto max-h-64 text-[10px] text-slate-700">{JSON.stringify(poaData, null, 2)}</pre>
            </details>
          )}
        </div>
      </Section>

      {/* 20. Loan Applications — LIVE from TAHDCO Scheme Portal. Allocation/Unspent stay manual (not in API). */}
      <Section num="20" title="Loan Applications" source="TAHDCO Scheme + TELP (LIVE)" sectionKey="sec23_loan_applications" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {tahdcoLoading && <p className="text-xs text-gray-500 mb-2">Loading TAHDCO data…</p>}
        {tahdcoError   && <p className="text-xs text-red-700 mb-2">TAHDCO API error: {tahdcoError} (showing manual entries)</p>}
        {telpLoading   && <p className="text-xs text-gray-500 mb-2">Loading TELP (education loans) data…</p>}
        {telpError     && <p className="text-xs text-red-700 mb-2">TELP API error: {telpError} (showing manual entries)</p>}

        {/* Order matches TAHDCO API scheme list — 10 schemes as of Aug 2026 */}
        {(() => {
          // Aggregate TELP by scheme code — either across all districts (state view)
          // or for the selected district only.
          const telpAgg = { 'EDU-NSKFDC': null, 'EDU-NSTFDC': null, 'EDU-NSFDC': null };
          // Upstream returns { status, data: { totalDistricts, data: [...] } };
          // our proxy wraps it as { success, data: <upstream> } — so rows live at
          // telpData.data.data. Also accept telpData.data if upstream shape changes.
          const telpRows = Array.isArray(telpData?.data?.data) ? telpData.data.data
                         : Array.isArray(telpData?.data)      ? telpData.data
                         : Array.isArray(telpData)            ? telpData
                         : [];
          const wantDist = (district || '').trim().toUpperCase();
          telpRows.forEach(dRow => {
            const dName = (dRow?.district?.name || '').trim().toUpperCase();
            if (wantDist && dName !== wantDist) return;
            (dRow?.schemes || []).forEach(sch => {
              const code = sch.schemeCode;
              if (!(code in telpAgg)) return;
              if (!telpAgg[code]) telpAgg[code] = { received: 0, approved: 0, pending: 0, rejected: 0, allocation: 0, unspent: 0 };
              telpAgg[code].received += Number(sch.received?.count)  || 0;
              telpAgg[code].approved += Number(sch.approved?.count)  || 0;
              telpAgg[code].pending  += Number(sch.pending?.count)   || 0;
              telpAgg[code].rejected += Number(sch.rejected?.count)  || 0;
              telpAgg[code].allocation += Number(sch.totalAllocationFy2025_26) || 0;
              telpAgg[code].unspent    += Number(sch.unspentFy2025_26)         || 0;
            });
          });

          const SCHEMES = [
            { apiKey: 'cmArise',     manualKey: 'cm_arise',           label: '(i) CM-Arise' },
            { apiKey: 'pmAjay',      manualKey: 'pm_ajay',            label: 'PM-AJAY' },
            { apiKey: 'pmAjayGroup', manualKey: 'pm_ajay_group',      label: 'PM-AJAY (Group)' },
            { apiKey: 'nnmt',        manualKey: 'land_nannilam',      label: '(ii) Land Development Scheme (NNMT)' },
            { apiKey: 'nmntO',       manualKey: 'land_nannilam_own',  label: '(iii) NNMT — Own Contribution' },
            { apiKey: 'lps',         manualKey: 'lps',                label: 'Land Purchase Scheme (LPS)' },
            { apiKey: 'shg',         manualKey: 'shg',                label: 'Economic Assistance to SHG' },
            { apiKey: 'ftps',        manualKey: 'ftps',               label: 'Fast Track Power Supply (FTPS)' },
            { apiKey: 'nskfdcGtl',   manualKey: 'nskfdc_gtl',         label: 'NSKFDC — General Term Loan' },
            { apiKey: 'nstfdcAsry',  manualKey: 'nstfdc_asry',        label: 'NSTFDC — Adivasi Shiksha Rrinn Yojana' },
            // Education Loans — sourced from TELP API (per client's sheet §20 rows 4-6)
            { telpCode: 'EDU-NSKFDC', manualKey: 'edu_nskfdc', label: 'Education Loan — NSKFDC (Safai Karmachari)' },
            { telpCode: 'EDU-NSTFDC', manualKey: 'edu_nstfdc', label: 'Education Loan — NSTFDC (ST)' },
            { telpCode: 'EDU-NSFDC',  manualKey: 'edu_nsfdc',  label: 'Education Loan — NSFDC (SC)' },
          ];
          // If the API returns extra scheme keys we don't have listed here,
          // render them at the end so nothing is silently dropped.
          // Guard: skip numeric keys (API sometimes returns an array shape where
          // Object.keys gives '0','1','2'... — those aren't real schemes).
          const apiSchemes = (tahdcoData?.data && !Array.isArray(tahdcoData.data)) ? tahdcoData.data : {};
          const knownApiKeys = new Set(SCHEMES.map(s => s.apiKey));
          Object.keys(apiSchemes).forEach(k => {
            if (/^\d+$/.test(k)) return; // skip numeric indices
            if (knownApiKeys.has(k)) return;
            const meta = apiSchemes[k];
            if (!meta || typeof meta !== 'object') return;
            // Only add if it actually looks like a scheme row (has one of the known fields)
            const looksLikeScheme = 'totalApplications' in meta || 'schemeName' in meta || 'schemeCode' in meta || 'schemePaymentCount' in meta;
            if (!looksLikeScheme) return;
            SCHEMES.push({ apiKey: k, manualKey: k.toLowerCase(), label: meta.schemeName || meta.schemeCode || k });
          });

          const numFmt = (n) => (n === undefined || n === null ? NA : fmt(n));
          const chooseLive = (manualKey, liveVal) => {
            const manual = mvFmt('sec23_loan_applications', manualKey);
            if (manual !== NA) return manual;
            return numFmt(liveVal);
          };
          const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';

          return (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                    <th className="px-4 py-2.5 w-1/4">Scheme</th>
                    <th className="px-4 py-2.5">Received</th>
                    <th className="px-4 py-2.5">Approved</th>
                    <th className="px-4 py-2.5">Pending</th>
                    <th className="px-4 py-2.5">Rejected</th>
                    <th className="px-4 py-2.5">Total Allocation for FY 2025-26</th>
                    <th className="px-4 py-2.5">Unspent for FY 2025-26</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {SCHEMES.map((s, i) => {
                    let received, approved, pending, rejected, allocLive, unspentLive, schemeCodeLabel;
                    if (s.telpCode) {
                      // Education Loans — data from TELP API
                      const t = telpAgg[s.telpCode];
                      if (t) {
                        received = t.received;
                        approved = t.approved;
                        pending  = t.pending;
                        rejected = t.rejected;
                        allocLive   = t.allocation || null;
                        unspentLive = t.unspent    || null;
                      }
                      schemeCodeLabel = s.telpCode;
                    } else {
                      const api = apiSchemes[s.apiKey] || {};
                      received  = api.totalApplications;
                      approved  = api.schemePaymentCount;
                      pending   = Object.keys(api).length
                        ? (api.dmPending || 0) + (api.inProgressCount || 0) + (api.forwardedCount || 0) + (api.savedCount || 0) + (api.submittedCount || 0)
                        : null;
                      rejected  = api.rejectedCount;
                      schemeCodeLabel = api.schemeCode;
                    }
                    const bg = i % 2 === 0 ? 'bg-slate-50' : 'bg-white';
                    const rec = chooseLive(`${s.manualKey}__received`, received);
                    const apr = chooseLive(`${s.manualKey}__approved`, approved);
                    const pen = chooseLive(`${s.manualKey}__pending`,  pending);
                    const rej = chooseLive(`${s.manualKey}__rejected`, rejected);
                    // Allocation & Unspent: prefer manual, fall back to TELP live (TAHDCO doesn't provide them)
                    const manualAlloc   = mvFmt('sec23_loan_applications', `${s.manualKey}__allocation`);
                    const manualUnspent = mvFmt('sec23_loan_applications', `${s.manualKey}__unspent`);
                    const alloc   = manualAlloc   !== NA ? manualAlloc   : (allocLive   != null ? fmt(allocLive)   : NA);
                    const unspent = manualUnspent !== NA ? manualUnspent : (unspentLive != null ? fmt(unspentLive) : NA);

                    // TELP drill-down: collect applications from every district row for this scheme + bucket.
                    const telpDrill = (bucket) => {
                      if (!s.telpCode) return;
                      const wantDist = (district || '').trim().toUpperCase();
                      const apps = [];
                      telpRows.forEach(dRow => {
                        const dName = (dRow?.district?.name || '').trim().toUpperCase();
                        if (wantDist && dName !== wantDist) return;
                        (dRow?.schemes || []).forEach(sch => {
                          if (sch.schemeCode !== s.telpCode) return;
                          (sch[bucket]?.applications || []).forEach(a => apps.push({ ...a, _district: dRow?.district?.name }));
                        });
                      });
                      const cols = [
                        { key: 'applicationNumber', label: 'Application No.' },
                        { key: 'studentName',       label: 'Student' },
                        { key: '_district',         label: 'District' },
                        { key: 'community',         label: 'Community' },
                        { key: 'gender',            label: 'Gender' },
                        { key: 'courseName',        label: 'Course' },
                        { key: 'institutionName',   label: 'Institution' },
                        { key: 'loanAgency',        label: 'Agency' },
                        { key: 'statusName',        label: 'Status' },
                        { key: 'disbursedAmount',   label: 'Disbursed (₹)' },
                        { key: 'appliedOn',         label: 'Applied On' },
                      ];
                      const bucketLabel = { received: 'Received', approved: 'Approved', pending: 'Pending', rejected: 'Rejected' }[bucket];
                      openDrill(`${s.label} — ${bucketLabel}`, apps, cols, `TELP · ${district || 'All Districts'}`);
                    };
                    const clickable = (v, bucket) => v !== NA && s.telpCode
                      ? { className: `px-4 py-2.5 ${cls(v)} cursor-pointer hover:bg-blue-50 hover:underline`, onClick: () => telpDrill(bucket) }
                      : { className: `px-4 py-2.5 ${cls(v)}` };

                    return (
                      <tr key={s.apiKey || s.telpCode} className={`${bg} hover:brightness-95 transition-all`}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {s.label}
                          {schemeCodeLabel && (
                            <span className="ml-2 text-[10px] text-gray-500 font-normal">{schemeCodeLabel}</span>
                          )}
                        </td>
                        <td {...clickable(rec, 'received')}>{rec}</td>
                        <td {...clickable(apr, 'approved')}>{apr}</td>
                        <td {...clickable(pen, 'pending')}>{pen}</td>
                        <td {...clickable(rej, 'rejected')}>{rej}</td>
                        <td className={`px-4 py-2.5 ${cls(alloc)}`}>{alloc}</td>
                        <td className={`px-4 py-2.5 ${cls(unspent)}`}>{unspent}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Super-admin debug — raw TAHDCO payload */}
        {isSuperAdmin && tahdcoData && (
          <details className="text-xs bg-slate-50 border border-slate-200 p-2 mt-3">
            <summary className="cursor-pointer text-slate-600 font-semibold">Raw TAHDCO API response (debug)</summary>
            <pre className="mt-2 overflow-auto max-h-64 text-[10px] text-slate-700">{JSON.stringify(tahdcoData, null, 2)}</pre>
          </details>
        )}
      </Section>

      {/* 21. Construction Status — LIVE from TIME (CIME) Portal. Occupancy stays manual (not in API). */}
      <Section num="21" title="Construction Status" source="TIME / CIME Portal (LIVE)" sectionKey="sec24_construction" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {timeLoading && <p className="text-xs text-gray-500 mb-2">Loading construction data…</p>}
        {timeError   && <p className="text-xs text-red-700 mb-2">TIME API error: {timeError} (showing manual entries)</p>}
        {(() => {
          // Filter TIME rows by selected district. TIME's district field is in title case;
          // dashboard's district is ALL-CAPS. Compare upper-cased.
          const wantDistrict = (district || '').toUpperCase().trim();
          const filteredRows = (timeData || []).filter(r => {
            if (!wantDistrict) return true;
            return String(r.districtName || '').toUpperCase().trim() === wantDistrict;
          });
          // Aggregate by mainCategory
          const agg = {};
          filteredRows.forEach(r => {
            const k = String(r.mainCategory || '').trim();
            if (!k) return;
            if (!agg[k]) agg[k] = { sanctioned:0, completed:0, under_const:0, not_started:0 };
            agg[k].sanctioned  += r.totalDivisionCount || 0;
            agg[k].completed   += r.completed          || 0;
            agg[k].under_const += r.inProgress         || 0;
            agg[k].not_started += r.notStarted         || 0;
          });
          // Chooser: live value first (if we have data for this category), fall back to manual
          const cellVal = (code, col, apiKey) => {
            const manual = mvFmt('sec24_construction', `${code}__${col}`);
            if (manual !== NA) return manual;
            const live = apiKey && agg[apiKey] ? agg[apiKey][col] : undefined;
            if (live !== undefined) return fmt(live);
            return NA;
          };
          // Rank tier for a TIME cell — reads across all districts in timeData.
          const timeRank = (apiCategory, col, direction) => {
            if (!apiCategory || !district) return '';
            const map = {};
            (timeData || []).forEach(r => {
              const cat = String(r.mainCategory || '').trim();
              if (cat !== apiCategory) return;
              const d = String(r.districtName || '').toUpperCase().trim();
              const v = col === 'sanctioned' ? r.totalDivisionCount
                      : col === 'completed'  ? r.completed
                      : col === 'under_const'? r.inProgress
                      : col === 'not_started'? r.notStarted
                      : null;
              if (v == null) return;
              map[d] = (map[d] || 0) + Number(v);
            });
            return rankCellClass(rankTier(map, district, direction));
          };
          return (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                    <th className="px-4 py-2.5 w-1/6">Portal &amp; Module</th>
                    <th className="px-4 py-2.5">Sanctioned for the year 2025-26</th>
                    <th className="px-4 py-2.5">Completed for the year 2025-26</th>
                    <th className="px-4 py-2.5">Under Construction</th>
                    <th className="px-4 py-2.5">Not Yet Started</th>
                    <th className="px-4 py-2.5">No. of Occupied &amp; Unoccupied</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { label: '(i) Hostel',                                       bg: 'bg-slate-50', code: 'hostel',            apiCategory: 'Hostel' },
                    { label: '(ii) School',                                      bg: 'bg-white',    code: 'school',            apiCategory: 'School' },
                    { label: '(iii) Community Hall (Village Knowledge Center)', bg: 'bg-slate-50', code: 'community_hall',    apiCategory: 'Community Hall' },
                    { label: '(iv) Shopping Complex',                            bg: 'bg-white',    code: 'shopping_complex',  apiCategory: null /* not in API */ },
                  ].map((row, i) => {
                    const s = cellVal(row.code, 'sanctioned',  row.apiCategory);
                    const c = cellVal(row.code, 'completed',   row.apiCategory);
                    const u = cellVal(row.code, 'under_const', row.apiCategory);
                    const n = cellVal(row.code, 'not_started', row.apiCategory);
                    // Occupancy is a post-completion metric not in TIME
                    const occ = mvFmt('sec24_construction', `${row.code}__occupancy`);
                    const cls = (v) => v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold';
                    // Drill-down: list the TIME rows that contributed, filtered by category
                    const openTimeDrill = (colLabel, colKey) => {
                      if (!row.apiCategory) return;
                      const wantDist = (district || '').trim().toUpperCase();
                      const rows = (timeData || []).filter(r => {
                        if (String(r.mainCategory || '').trim() !== row.apiCategory) return false;
                        if (wantDist && String(r.districtName || '').toUpperCase().trim() !== wantDist) return false;
                        return true;
                      });
                      const cols = [
                        { key: 'districtName',       label: 'District' },
                        { key: 'divisionName',       label: 'Division' },
                        { key: 'mainCategory',       label: 'Category' },
                        { key: 'subCategory',        label: 'Sub-Category' },
                        { key: 'totalDivisionCount', label: 'Sanctioned' },
                        { key: 'completed',          label: 'Completed' },
                        { key: 'inProgress',         label: 'Under Construction' },
                        { key: 'notStarted',         label: 'Not Started' },
                      ];
                      openDrill(`${row.label} — ${colLabel}`, rows, cols, `TIME · ${district || 'All Districts'}`);
                    };
                    const clickableTime = (v, colLabel, colKey) => v !== NA && row.apiCategory
                      ? { className: `px-4 py-2.5 ${cls(v)} cursor-pointer hover:bg-blue-50 hover:underline`, onClick: () => openTimeDrill(colLabel, colKey) }
                      : { className: `px-4 py-2.5 ${cls(v)}` };
                    return (
                      <tr key={i} className={`${row.bg} hover:brightness-95 transition-all`}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                        <td {...clickableTime(s, 'Sanctioned', 'totalDivisionCount')}>{s}</td>
                        <td className={`${clickableTime(c, 'Completed', 'completed').className} ${timeRank(row.apiCategory,'completed','higherIsBetter')}`} onClick={clickableTime(c, 'Completed', 'completed').onClick}>{c}</td>
                        <td className={`${clickableTime(u, 'Under Construction', 'inProgress').className} ${timeRank(row.apiCategory,'under_const','lowerIsBetter')}`} onClick={clickableTime(u, 'Under Construction', 'inProgress').onClick}>{u}</td>
                        <td className={`${clickableTime(n, 'Not Yet Started', 'notStarted').className} ${timeRank(row.apiCategory,'not_started','lowerIsBetter')}`} onClick={clickableTime(n, 'Not Yet Started', 'notStarted').onClick}>{n}</td>
                        <td className={`px-4 py-2.5 ${cls(occ)}`}>{occ}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Super-admin debug — raw TIME payload */}
              {isSuperAdmin && timeData && (
                <details className="text-xs bg-slate-50 border border-slate-200 p-2 mt-3">
                  <summary className="cursor-pointer text-slate-600 font-semibold">Raw TIME API response (debug)</summary>
                  <pre className="mt-2 overflow-auto max-h-64 text-[10px] text-slate-700">{JSON.stringify(filteredRows, null, 2)}</pre>
                </details>
              )}
            </div>
          );
        })()}
      </Section>

      {/* 22. FRA — Forest Rights Act — LIVE from FRA Portal (state or district-wise) */}
      <Section num="22" title="FRA — Forest Rights Act" source={`FRA Portal (LIVE — ${district ? district : 'STATE'})`} sectionKey="sec25_fra" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        {fraLoading && <p className="text-xs text-gray-500 mb-2">Loading FRA data…</p>}
        {fraError   && <p className="text-xs text-red-700 mb-2">FRA API error: {fraError} (showing manual entries)</p>}
        {district && !FRA_DISTRICT_CODES[district] && (
          <p className="text-xs text-amber-700 mb-2">Note: FRA portal has no mapping for &quot;{district}&quot; — showing state totals.</p>
        )}
        <div className="space-y-4">
          {/* Sub-table 1 */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
              Claims &amp; FRC Verification
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Total Claims Received</th>
                  <th className="px-4 py-2.5">FRC Verification Completed</th>
                  <th className="px-4 py-2.5">FRC Verification Pending</th>
                  <th className="px-4 py-2.5">Claims Recommended for Survey</th>
                  <th className="px-4 py-2.5">Claims Pending for Survey</th>
                  <th className="px-4 py-2.5">Claims Recommended to SDLC</th>
                  <th className="px-4 py-2.5">Claims Pending for Resolution (Submit to SDLC)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  {[
                    ['claims_received',          ['TOTAL_CLAIMS_RECEIVED'],       'Total Claims Received'],
                    ['frc_completed',            ['FRC_VERIFICATION_COMPLETED'],  'FRC Verification Completed'],
                    ['frc_pending',              ['FRC_VERIFICATION_PENDING'],    'FRC Verification Pending'],
                    ['recommended_survey',       ['CLAIMS_RECOMMENDED_SURVEY'],   'Claims Recommended for Survey'],
                    ['pending_survey',           ['CLAIMS_PENDING_SURVEY'],       'Claims Pending for Survey'],
                    ['recommended_sdlc',         ['CLAIMS_RECOMMENDED_SDLC'],     'Claims Recommended to SDLC'],
                    ['pending_resolution_sdclc', ['CLAIMS_PENDING_RESOLUTION'],   'Claims Pending for Resolution'],
                  ].map(([mkey, aliases, label], i) => {
                    const v = fraFmt(mkey, aliases);
                    const clickable = v !== NA;
                    return (
                      <td key={i}
                          onClick={clickable ? () => openFraDetail(label) : undefined}
                          title={clickable ? 'Click to view details' : ''}
                          className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedFra(mkey, aliases[0])} ${clickable ? 'cursor-pointer hover:bg-blue-50 hover:underline' : ''}`}>
                        {v}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sub-table 2 */}
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-700 uppercase tracking-wide">
              SDLC / DLC / Titles
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-600 font-semibold align-top">
                  <th className="px-4 py-2.5">Claims Recommended to DLC</th>
                  <th className="px-4 py-2.5">Claims Pending with SDLC</th>
                  <th className="px-4 py-2.5">Claims Approved by DLC</th>
                  <th className="px-4 py-2.5">Claims Pending with DLC</th>
                  <th className="px-4 py-2.5">Titles Issued</th>
                  <th className="px-4 py-2.5">Titles Pending</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-50">
                  {[
                    ['recommended_dlc',  ['CLAIMS_RECOMMENDED_DLC'], 'Claims Recommended to DLC'],
                    ['pending_sdlc',     ['CLAIMS_PENDING_SDLC'],    'Claims Pending with SDLC'],
                    ['approved_dlc',     ['CLAIMS_APPROVED_DLC'],    'Claims Approved by DLC'],
                    ['pending_dlc',      ['CLAIMS_PENDING_DLC'],     'Claims Pending with DLC'],
                    ['titles_issued',    ['TITLES_ISSUED'],          'Titles Issued'],
                    ['titles_pending',   ['TITLES_PENDING'],         'Titles Pending'],
                  ].map(([mkey, aliases, label], i) => {
                    const v = fraFmt(mkey, aliases);
                    const clickable = v !== NA;
                    return (
                      <td key={i}
                          onClick={clickable ? () => openFraDetail(label) : undefined}
                          title={clickable ? 'Click to view details' : ''}
                          className={`px-4 py-2.5 ${v === NA ? 'text-gray-400' : 'text-gray-800 font-semibold'} ${rankedFra(mkey, aliases[0])} ${clickable ? 'cursor-pointer hover:bg-blue-50 hover:underline' : ''}`}>
                        {v}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Raw payload — super admin only, collapsed by default. Helps map exact API keys. */}
          {isSuperAdmin && fraData && (
            <details className="text-xs bg-slate-50 border border-slate-200 p-2">
              <summary className="cursor-pointer text-slate-600 font-semibold">Raw FRA API response (debug)</summary>
              <pre className="mt-2 overflow-auto max-h-64 text-[10px] text-slate-700">{JSON.stringify(fraData, null, 2)}</pre>
            </details>
          )}
        </div>

        {/* FRA drill-down modal — opens on cell click, calls FRA API TYPEID=3009 */}
        {fraDetailOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setFraDetailOpen(false)}>
            <div className="bg-white border border-slate-300 shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 py-3 bg-[#0f2e4d] text-white flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-blue-200">FRA Details {district ? `— ${district}` : '— State-wide'}</div>
                  <div className="text-sm font-semibold">{fraDetailTitle}</div>
                </div>
                <button className="text-white hover:bg-white/20 px-2 py-1" onClick={() => setFraDetailOpen(false)}>✕</button>
              </div>
              <div className="p-4 overflow-auto flex-1">
                {fraDetailLoading && <p className="text-sm text-gray-500">Loading details…</p>}
                {fraDetailError && <p className="text-sm text-rose-700">Error: {fraDetailError}</p>}
                {!fraDetailLoading && !fraDetailError && fraDetailRows.length === 0 && (
                  <p className="text-sm text-gray-500">No detail records returned.</p>
                )}
                {!fraDetailLoading && !fraDetailError && fraDetailRows.length > 0 && (() => {
                  const cols = [
                    { key: 'FRA_APPLICATION_ID',        label: 'Application ID' },
                    { key: 'TH_NAME',                   label: 'Applicant' },
                    { key: 'DISTRICT_NAME_ENG',         label: 'District' },
                    { key: 'TALUKA_NAME_ENG',           label: 'Taluk' },
                    { key: 'REVENUE_DIVISION_NAME_ENG', label: 'Revenue Division' },
                    { key: 'GRAM_SABHA_ENG',            label: 'Gram Sabha' },
                    { key: 'APPLICATION_EXTENT_HECTARES', label: 'Extent (Ha)' },
                    { key: 'FRC_RECOMMENDATION_STATUS', label: 'FRC Status' },
                    { key: 'SURVEY_VERIFICATION_ST',    label: 'Survey' },
                    { key: 'STATUS_OF_GS',              label: 'GS' },
                    { key: 'STATUS_OF_SDLC',            label: 'SDLC' },
                    { key: 'STATUS_OF_DLC',             label: 'DLC' },
                    { key: 'TITLE_STATUS',              label: 'Title' },
                  ];
                  const q = fraDetailQuery.trim().toLowerCase();
                  const filtered = q
                    ? fraDetailRows.filter(r => cols.some(c => String(r?.[c.key] ?? '').toLowerCase().includes(q)))
                    : fraDetailRows;
                  const totalPages = Math.max(1, Math.ceil(filtered.length / FRA_DETAIL_PAGE_SIZE));
                  const page = Math.min(fraDetailPage, totalPages);
                  const start = (page - 1) * FRA_DETAIL_PAGE_SIZE;
                  const pageRows = filtered.slice(start, start + FRA_DETAIL_PAGE_SIZE);
                  const showingFrom = filtered.length === 0 ? 0 : start + 1;
                  const showingTo   = Math.min(start + FRA_DETAIL_PAGE_SIZE, filtered.length);
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          placeholder="Search across all fields..."
                          value={fraDetailQuery}
                          onChange={(e) => { setFraDetailQuery(e.target.value); setFraDetailPage(1); }}
                          className="flex-1 min-w-[200px] px-2 py-1 border border-slate-300 text-xs focus:outline-none focus:border-[#0f2e4d]"
                        />
                        <span className="text-[11px] text-slate-600">
                          {filtered.length === fraDetailRows.length
                            ? `${filtered.length.toLocaleString('en-IN')} records`
                            : `${filtered.length.toLocaleString('en-IN')} of ${fraDetailRows.length.toLocaleString('en-IN')} records`}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-slate-200">
                          <thead className="bg-slate-100 border-b border-slate-300 sticky top-0">
                            <tr>
                              <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-600 font-semibold w-10">#</th>
                              {cols.map(c => (
                                <th key={c.key} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-600 font-semibold whitespace-nowrap">{c.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {pageRows.map((r, i) => (
                              <tr key={start + i} className="hover:bg-slate-50">
                                <td className="px-2 py-1.5 font-mono text-slate-500">{start + i + 1}</td>
                                {cols.map(c => {
                                  const v = r?.[c.key];
                                  return (
                                    <td key={c.key} className="px-2 py-1.5 text-slate-800 whitespace-nowrap">
                                      {v == null || v === '' ? '—' : String(v)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                            {pageRows.length === 0 && (
                              <tr><td colSpan={cols.length + 1} className="px-4 py-6 text-center text-slate-500 text-xs">No records match this search.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination controls */}
                      <div className="flex items-center justify-between text-[11px] text-slate-600 flex-wrap gap-2 pt-1">
                        <span>Showing {showingFrom}-{showingTo} of {filtered.length.toLocaleString('en-IN')}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setFraDetailPage(1)} disabled={page <= 1}
                            className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                          >« First</button>
                          <button
                            onClick={() => setFraDetailPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                            className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                          >‹ Prev</button>
                          <span className="px-2">Page {page} of {totalPages}</span>
                          <button
                            onClick={() => setFraDetailPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                            className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                          >Next ›</button>
                          <button
                            onClick={() => setFraDetailPage(totalPages)} disabled={page >= totalPages}
                            className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
                          >Last »</button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* 23. Other Issues */}
      <Section num="23" title="Other Issues (New Schemes / Ideas / Immediate action needs to be taken if any)" source="Manual Entry" sectionKey="sec26_other" canEdit={isSuperAdmin || districtLocked} onEdit={setEditingSection}>
        <div className="bg-white border border-gray-200 px-4 py-3">
          {mv('sec26_other', 'notes')
            ? <p className="text-sm text-gray-800 whitespace-pre-wrap">{mv('sec26_other', 'notes')}</p>
            : <p className="text-xs text-gray-400 italic">No data — awaiting manual entry.</p>}
        </div>
      </Section>

      {/* Manual entry modal (only shown while editing) */}
      {editingSection && (
        <ManualEntryModal
          sectionKey={editingSection}
          district={manualDistrict}
          initialValues={modalInitialValues}
          onClose={() => setEditingSection(null)}
          onSaved={refetchManual}
        />
      )}

      {/* Header-card drill-down modal */}
      {drill && (
        <HabitationDetailModal
          kind={drill.kind}
          district={district || ''}
          filter={drill.filter || drill.mgmt}
          filterLabel={drill.label}
          stream={drill.stream}
          onClose={() => setDrill(null)}
        />
      )}

      {/* Generic drill-down modal (shared by Construction, Education Loan, DLVMC, SDLVMC, POA, Land) */}
      {drillDown && (
        <DetailModal
          open={!!drillDown}
          onClose={() => setDrillDown(null)}
          title={drillDown.title}
          subtitle={drillDown.subtitle}
          rows={drillDown.rows}
          columns={drillDown.columns}
        />
      )}
    </div>
  );
};

export default IntegratedDashboard;
